<template>
  <div>
    <!-- Loading State -->
    <b-row align-h="center" v-if="dataLoading">
      <b-spinner
        class="mt-4"
        style="width: 4rem; height: 4rem"
        type="grow"
        variant="primary"
        label="Loading..."
      ></b-spinner>
    </b-row>

    <!-- Main Content -->
    <div v-else>
      <!-- Filter Controls -->
      <div class="planner-filter-wrap mb-3">
        <div class="gradient-line-wrapper"></div>
        <b-card class="planner-filter-card border-0">
        <div class="d-flex justify-content-between align-items-center mb-3 planner-filter-header">
          <div>
            <h5 class="mb-0 resource-title">Daily Wise Planner</h5>
            <small class="text-muted">Filter by class, subject, topic and subtopic</small>
          </div>
          <div>
            <b-button
              v-if="rights && rights.add"
              variant="info"
              size="sm"
              @click="openImportModal()"
              class="mr-2"
              v-b-tooltip.hover
              title="Import Planner Data from Excel"
            >
              Import Data
            </b-button>
            <b-button
              variant="success"
              size="sm"
              @click="openExportModal()"
              class="mr-2"
              :disabled="!canExportData"
              v-b-tooltip.hover
              :title="
                canExportData
                  ? 'Export Planner Data to Excel'
                  : 'Load data first to enable export'
              "
            >
              Export Data
            </b-button>
            <b-button
              variant="primary"
              size="sm"
              @click="openPrintView()"
              :disabled="!canPrint"
              v-b-tooltip.hover
              title="View planner (Select class, section, session and subject first)"
            >
              View Planner
            </b-button>
          </div>
        </div>

        <b-row class="planner-filter-row">
          <b-col xl="2" lg="3" md="6" sm="12">
            <b-form-group label="Class" label-class="planner-filter-label">
              <v-select
                v-model="myObj.clsID"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="classesOptions"
                :clearable="false"
                label="name"
                :reduce="(val) => val.id"
                placeholder="Select class"
                append-to-body
                @input="FillSections()"
              />
            </b-form-group>
          </b-col>

          <b-col xl="2" lg="3" md="6" sm="12">
            <b-form-group label="Section" label-class="planner-filter-label">
              <v-select
                v-model="secIDs"
                :key="'section-' + myObj.clsID + '-' + currentSections.length"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="currentSections"
                :reduce="(val) => val.id"
                label="section"
                :clearable="false"
                placeholder="Select section"
                append-to-body
                @input="fillSubjects()"
              />
            </b-form-group>
          </b-col>

          <b-col xl="2" lg="3" md="6" sm="12">
            <b-form-group label="Session" label-class="planner-filter-label">
              <v-select
                v-model="myObj.sessionID"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="sessions"
                label="session"
                :reduce="(val) => val.id"
                :clearable="false"
                placeholder="Select session"
                append-to-body
                @input="onSessionChange($event)"
              />
            </b-form-group>
          </b-col>

          <b-col xl="2" lg="3" md="6" sm="12">
            <b-form-group label="Subject" label-class="planner-filter-label">
              <v-select
                v-model="myObj.subjectID"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="subjects"
                :clearable="false"
                label="subject"
                :reduce="(val) => val.id"
                placeholder="Select subject"
                append-to-body
                :disabled="isTeacherScoped && !myObj.clsID"
                @input="loadTopics()"
              />
            </b-form-group>
          </b-col>

          <b-col xl="2" lg="3" md="6" sm="12">
            <b-form-group label="Topic" label-class="planner-filter-label">
              <v-select
                v-model="filterObj.topicID"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="topicFilterOptions"
                :clearable="false"
                label="name"
                :reduce="(val) => val.id"
                placeholder="Select topic"
                append-to-body
                @input="loadSubtopics()"
              />
            </b-form-group>
          </b-col>

          <b-col xl="2" lg="3" md="6" sm="12">
            <b-form-group label="Subtopic" label-class="planner-filter-label">
              <v-select
                v-model="filterObj.subtopicID"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="subtopicFilterOptions"
                :clearable="false"
                label="name"
                :reduce="(val) => val.id"
                placeholder="Select subtopic"
                append-to-body
                @input="loadPlannerSheet()"
              />
            </b-form-group>
          </b-col>
        </b-row>
        </b-card>
      </div>

      <!-- Subtopics Table -->
      <div v-if="showPlannerData && !showDailyPlanTable">
        <div class="subtopics-planner-wrap">
          <div class="gradient-line-wrapper"></div>
          <b-card class="subtopics-planner-card border-0">
            <div
              class="d-flex justify-content-between align-items-center mb-3 subtopics-header"
            >
              <div>
                <h5 class="mb-0 resource-title">Available Subtopics</h5>
                <small class="text-muted"
                  >Select a subtopic to view or create daily lesson plans</small
                >
              </div>
              <div class="d-flex align-items-center subtopics-header-actions">
                <b-badge
                  variant="light-primary"
                  class="subtopics-count-badge mr-2"
                  v-if="subtopics.length || otherLoading"
                >
                  <template v-if="otherLoading">Loading...</template>
                  <template v-else>
                    {{ subtopics.length }}
                    {{ subtopics.length === 1 ? "subtopic" : "subtopics" }}
                  </template>
                </b-badge>

                <div
                  v-if="canReplicate"
                  class="position-relative replicate-action-wrap"
                >
                  <transition name="fade">
                    <b-badge
                      v-if="selectedSubtopicKeys.length > 0"
                      variant="primary"
                      class="selection-dot-badge cursor-pointer"
                      @click="clearSubtopicSelection"
                    >
                      {{ selectedSubtopicKeys.length }}
                    </b-badge>
                  </transition>

                  <b-button
                    variant="outline-primary"
                    size="sm"
                    class="replicate-btn"
                    :disabled="!canOpenReplicate"
                    @click="openReplicateModal"
                  >
                    <feather-icon icon="CopyIcon" size="16" class="mr-50" />
                    Replicate
                  </b-button>
                </div>
              </div>
            </div>

          <!-- Loading state -->
          <div v-if="otherLoading" class="subtopics-skeleton-wrap">
            <table class="table subtopics-skeleton-table mb-0">
              <thead>
                <tr>
                  <th
                    v-for="field in subtopicFields"
                    :key="'sk-head-' + field.key"
                  >
                    <div class="skeleton-loader skeleton-loader--sm"></div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="n in 6"
                  :key="'subtopic-skeleton-' + n"
                  class="skeleton-row"
                >
                  <td
                    v-for="(field, index) in subtopicFields"
                    :key="'subtopic-skeleton-cell-' + n + '-' + index"
                    class="skeleton-cell"
                  >
                    <div
                      class="skeleton-loader"
                      :style="{ width: getSubtopicSkeletonWidth(index) }"
                    ></div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Empty state -->
          <div
            v-else-if="!subtopics || subtopics.length === 0"
            class="text-center p-5"
          >
            <b-icon
              icon="book"
              class="mb-2"
              style="font-size: 3rem; color: #e0e6ed"
            ></b-icon>
            <h6 class="text-muted mb-2">No subtopics available</h6>
            <p class="text-muted small mb-0">
              Select Subject to load available subtopics, or try different
              filter combinations.
            </p>
          </div>

          <!-- Subtopics Table -->
          <div v-else class="subtopics-table-wrap">
            <b-table
              :items="subtopics"
              :fields="subtopicFields"
              responsive
              hover
              class="mb-0 subtopics-table"
              show-empty
              empty-text="No subtopics found"
            >
              <template v-if="canReplicate" #head(selected)>
                <b-form-checkbox
                  :checked="isAllSubtopicsSelected"
                  :indeterminate="isSubtopicsIndeterminate"
                  @change="selectAllSubtopics"
                />
              </template>

              <template v-if="canReplicate" #cell(selected)="{ item }">
                <b-form-checkbox
                  :checked="isSubtopicSelected(item)"
                  :disabled="!hasSavedDailyPlanner(item)"
                  @change="toggleSubtopicSelection(item)"
                />
              </template>

              <template #cell(sno)="data">
                <span class="subtopic-sno">{{ data.index + 1 }}</span>
              </template>

              <template #cell(mainTopicName)="{ item }">
                <span class="topic-pill" :title="item.mainTopicName">
                  {{ item.mainTopicName }}
                </span>
              </template>

              <template #cell(name)="{ item }">
                <span class="subtopic-name-text" :title="item.name">{{
                  item.name
                }}</span>
              </template>

              <template #cell(status)="{ item }">
                <b-badge
                  v-if="hasSavedDailyPlanner(item)"
                  variant="light-success"
                  class="status-pill status-pill--planned"
                >
                  <feather-icon icon="CheckCircleIcon" size="12" class="mr-25" />
                  {{ getSubtopicPlanCount(item) }}
                  {{ getSubtopicPlanCount(item) === 1 ? "plan" : "plans" }}
                </b-badge>
                <b-badge v-else variant="light-secondary" class="status-pill status-pill--pending">
                  Not planned
                </b-badge>
              </template>

              <template #cell(actions)="{ item }">
                <div class="subtopic-actions">
                  <b-button
                    v-if="rights && rights.view && hasSavedDailyPlanner(item)"
                    variant="outline-info"
                    size="sm"
                    class="action-btn-view"
                    @click.stop="viewDailyPlanner(item)"
                  >
                    <feather-icon icon="EyeIcon" size="14" />
                    <span>View</span>
                  </b-button>

                  <b-button
                    v-if="rights && rights.add"
                    variant="primary"
                    size="sm"
                    class="action-btn-add"
                    @click.stop="addDailyPlanner(item)"
                  >
                    <feather-icon icon="PlusIcon" size="14" />
                    <span>Add</span>
                  </b-button>

                  <span
                    v-if="!canShowSubtopicActions(item)"
                    class="text-muted small"
                    >—</span
                  >
                </div>
              </template>
            </b-table>
          </div>
          </b-card>
        </div>
      </div>

      <!-- Daily Planning Interface -->
      <div v-if="showDailyPlanTable">
        <!-- Back Button -->
        <div class="daily-planner-nav-wrap mb-3">
          <div class="daily-planner-nav-inner">
            <b-button
              variant="outline-secondary"
              size="sm"
              class="daily-back-btn"
              @click="goBackToSubtopics()"
              v-b-tooltip.hover
              title="Back to Subtopics"
            >
              <feather-icon icon="ArrowLeftIcon" size="14" class="mr-50" />
              Back to Subtopics
            </b-button>
          </div>
        </div>

        <!-- Daily Wise Planner Overview -->
        <div class="daily-overview-wrap mb-3">
          <b-card class="daily-overview-card border-0">
            <div class="daily-overview-header">
              <div>
                <h5 class="mb-0 resource-title">Daily Wise Planner Overview</h5>
                <small class="text-muted"
                  >Summary for the selected subtopic and lesson plans</small
                >
              </div>
              <div class="daily-overview-header-actions">
                <b-badge
                  v-if="dailyPlannerViewMode"
                  variant="light-warning"
                  class="mode-badge"
                >
                  <feather-icon icon="EyeIcon" size="12" class="mr-25" />
                  View Only
                </b-badge>
                <b-badge v-else variant="light-primary" class="mode-badge">
                  <feather-icon icon="EditIcon" size="12" class="mr-25" />
                  Edit Mode
                </b-badge>
                <b-badge variant="light-success" class="overview-topic-badge">{{
                  selectedSubtopicForPlan
                }}</b-badge>
                <b-badge
                  variant="light-info"
                  class="overview-count-badge"
                  v-if="dailyPlannerForms.length > 0"
                >
                  {{ dailyPlannerForms.length }}
                  {{ dailyPlannerForms.length === 1 ? "Plan" : "Plans" }}
                </b-badge>
              </div>
            </div>

            <div class="daily-overview-meta-grid">
              <div class="overview-meta-item">
                <span class="overview-meta-label">Grade</span>
                <span class="overview-meta-value">{{
                  selectedGrade || "Select class above"
                }}</span>
              </div>
              <div class="overview-meta-item">
                <span class="overview-meta-label">Subject</span>
                <span class="overview-meta-value">{{
                  selectedSubject || "Select subject above"
                }}</span>
              </div>
              <div class="overview-meta-item">
                <span class="overview-meta-label">Section</span>
                <span class="overview-meta-value">{{
                  selectedSection || "Select section above"
                }}</span>
              </div>
              <div class="overview-meta-item">
                <span class="overview-meta-label">Session</span>
                <span class="overview-meta-value">{{
                  selectedSession || "Select session above"
                }}</span>
              </div>
              <div class="overview-meta-item overview-meta-item--wide">
                <span class="overview-meta-label">Topic</span>
                <span class="overview-meta-value">{{
                  selectedTopic || "Select topic above"
                }}</span>
              </div>
            </div>

            <div class="daily-overview-section">
              <span class="overview-section-label">Pedagogy Methods</span>
              <div class="overview-tag-list">
                <template
                  v-if="plannerData.pedagogy && plannerData.pedagogy.length"
                >
                  <b-badge
                    v-for="pedagogy in plannerData.pedagogy"
                    :key="pedagogy"
                    variant="light-primary"
                    class="overview-tag-badge"
                  >
                    {{ pedagogy }}
                  </b-badge>
                </template>
                <template v-else>
                  <b-badge variant="light-primary" class="overview-tag-badge"
                    >Play-based learning</b-badge
                  >
                  <b-badge variant="light-primary" class="overview-tag-badge"
                    >Visual learning</b-badge
                  >
                  <b-badge variant="light-primary" class="overview-tag-badge"
                    >Collaborative learning</b-badge
                  >
                </template>
              </div>
            </div>

            <div class="daily-overview-section" v-if="plannerData.objectives">
              <span class="overview-section-label">Learning Objectives</span>
              <div class="overview-objectives-box">
                <p class="mb-0">{{ plannerData.objectives }}</p>
              </div>
            </div>
          </b-card>
        </div>

        <!-- Daily Planning Form -->
        <div class="daily-lesson-wrap">
          <b-card class="daily-lesson-card border-0">
            <div class="daily-lesson-header">
              <div>
                <h5 class="mb-0 resource-title">
                  {{
                    dailyPlannerViewMode
                      ? "Daily Lesson Plans"
                      : "Daily Lesson Planning"
                  }}
                </h5>
                <small class="text-muted">
                  {{
                    dailyPlannerViewMode
                      ? "Review saved lesson plans for this subtopic"
                      : "Create and manage lesson plans for this subtopic"
                  }}
                </small>
              </div>

              <div class="enhanced-toggle-switch">
                <input
                  type="checkbox"
                  id="globalColumnToggle"
                  :checked="globalColumnLayout === 3"
                  @change="toggleGlobalColumnLayout"
                  class="enhanced-toggle-input"
                />
                <label for="globalColumnToggle" class="enhanced-toggle-label">
                  <span class="toggle-slider"></span>
                  <span class="toggle-text-en">{{
                    globalColumnLayout === 3 ? "English" : "English"
                  }}</span>
                  <span class="toggle-text-ur">{{
                    globalColumnLayout === 3 ? "اردو" : "اردو"
                  }}</span>
                </label>
              </div>
            </div>

          <!-- Multiple Forms Container -->
          <!-- Loading State -->
          <div v-if="otherLoading" class="daily-lesson-state daily-lesson-state--loading">
            <b-spinner variant="primary" class="mb-3"></b-spinner>
            <p class="text-muted mb-0">Loading lesson plans...</p>
            <small class="text-muted"
              >Please wait while we fetch your data</small
            >
          </div>

          <!-- No Data State -->
          <div
            v-else-if="!otherLoading && dailyPlannerForms.length === 0"
            class="daily-lesson-state daily-lesson-state--empty"
          >
            <feather-icon
              icon="FileTextIcon"
              size="42"
              class="daily-lesson-state-icon mb-2"
            />
            <p class="text-muted mb-2">
              {{
                dailyPlannerViewMode
                  ? "No saved lesson plans for this subtopic"
                  : "No lesson plans created yet"
              }}
            </p>
            <small v-if="!dailyPlannerViewMode" class="text-muted"
              >Click "Create First Plan" below to start planning your
              lessons</small
            >
          </div>

          <!-- Forms Container -->
          <div v-else class="daily-lesson-forms">
            <div
              v-for="(form, formIndex) in dailyPlannerForms"
              :key="formIndex"
              class="planner-form-card daily-lesson-plan-card mb-3"
              :class="{ 'view-mode': dailyPlannerViewMode }"
            >
              <b-card class="daily-lesson-plan-inner border-0">
                <template #header>
                  <div
                    class="d-flex justify-content-between align-items-center daily-lesson-plan-header"
                  >
                    <h6 class="mb-0">Lesson Plan {{ formIndex + 1 }}</h6>
                    <b-button
                      v-if="
                        !dailyPlannerViewMode &&
                        dailyPlannerForms.length > 1 &&
                        rights &&
                        rights.delete
                      "
                      variant="outline-danger"
                      size="sm"
                      @click="removePlannerForm(formIndex)"
                      v-b-tooltip.hover
                      :title="
                        deletingForms.includes(formIndex)
                          ? 'Deleting...'
                          : 'Remove this lesson plan'
                      "
                      :disabled="deletingForms.includes(formIndex)"
                    >
                      <b-spinner
                        v-if="deletingForms.includes(formIndex)"
                        small
                        class="mr-1"
                      />
                      <feather-icon v-else icon="Trash2Icon" class="mr-1" />
                      {{
                        deletingForms.includes(formIndex)
                          ? "Deleting..."
                          : "Remove"
                      }}
                    </b-button>
                  </div>
                </template>

                <!-- Date Range Controls for this form -->
                <b-row class="mb-4 daily-lesson-date-row">
                  <b-col md="4">
                    <b-form-group
                      label="From Date"
                      label-class="daily-lesson-field-label"
                    >
                      <flat-pickr
                        v-model="form.dtFrom"
                        :config="getDateConfig(dailyPlannerViewMode)"
                        class="form-control"
                        :class="{ 'view-only-field': dailyPlannerViewMode }"
                        placeholder="Select from date"
                        :disabled="dailyPlannerViewMode"
                      />
                      <small
                        class="text-muted d-block mt-1"
                        v-if="form.dayFromName"
                      >
                        <b-icon icon="calendar" class="mr-1"></b-icon>
                        {{ form.dayFromName }}
                      </small>
                    </b-form-group>
                  </b-col>

                  <b-col md="4">
                    <b-form-group
                      label="To Date"
                      label-class="daily-lesson-field-label"
                    >
                      <flat-pickr
                        v-model="form.dtTo"
                        :config="getDateConfig(dailyPlannerViewMode)"
                        class="form-control"
                        :class="{ 'view-only-field': dailyPlannerViewMode }"
                        placeholder="Select to date"
                        :disabled="dailyPlannerViewMode"
                      />
                      <small
                        class="text-muted d-block mt-1"
                        v-if="form.dayToName"
                      >
                        <b-icon icon="calendar" class="mr-1"></b-icon>
                        {{ form.dayToName }}
                      </small>
                    </b-form-group>
                  </b-col>

                  <b-col md="4">
                    <b-form-group
                      label="Duration"
                      label-class="daily-lesson-field-label"
                    >
                      <b-form-input
                        v-model="form.duration"
                        placeholder="Enter duration (e.g., 5 days)"
                        class="form-control"
                        :readonly="dailyPlannerViewMode"
                        :class="{ 'view-only-field': dailyPlannerViewMode }"
                      />
                      <small
                        v-if="!dailyPlannerViewMode"
                        class="text-muted d-block mt-1"
                      >
                        <b-icon icon="clock" class="mr-1"></b-icon>
                        Enter the duration for this lesson plan
                      </small>
                    </b-form-group>
                  </b-col>
                </b-row>

                <!-- Lesson Planning Table for this form -->
                <div class="lesson-planning-section daily-lesson-table-section">
                  <div
                    class="d-flex justify-content-between align-items-center mb-3"
                  >
                    <h6 class="mb-0 daily-lesson-section-title">Lesson Activities</h6>
                  </div>

                  <div class="ht-theme-main table-responsive daily-lesson-table-wrap">
                    <HotTable
                      :key="`hot_${formIndex}_${dailyPlannerViewMode ? 'view' : 'edit'}_${globalColumnLayout}`"
                      :ref="`hotTable_${formIndex}`"
                      :data="form.tableData"
                      :colHeaders="getColumnHeaders(globalColumnLayout)"
                      :settings="getDynamicHotSettingsForForm(formIndex)"
                      class="handsontable-custom"
                      :class="{ 'handsontable-view-only': dailyPlannerViewMode }"
                      @afterChange="
                        (changes, source) =>
                          onTableDataChange(formIndex, changes, source)
                      "
                    />
                  </div>
                </div>

                <!-- Removed footer with individual buttons -->
              </b-card>
            </div>
          </div>

          <!-- Single Add and Save buttons at the bottom -->
          <div
            v-if="!dailyPlannerViewMode"
            class="daily-lesson-actions"
          >
            <b-button
              v-if="rights && rights.add"
              variant="primary"
              @click="addNewPlannerForm"
              :disabled="request || otherLoading"
              class="daily-lesson-action-btn"
              v-b-tooltip.hover
              :title="
                dailyPlannerForms.length === 0
                  ? 'Create your first lesson plan'
                  : 'Add another lesson plan'
              "
            >
              <feather-icon icon="PlusIcon" class="mr-1" />
              {{
                dailyPlannerForms.length === 0
                  ? "Create First Plan"
                  : "Add Another Plan"
              }}
            </b-button>

            <b-button
              variant="success"
              @click="saveAllPlannerForms"
              :disabled="request || isAnySaving || otherLoading"
              class="daily-lesson-action-btn"
              v-b-tooltip.hover
              title="Save all lesson plans"
            >
              <b-spinner v-if="isAnySaving" small type="grow" class="mr-1" />
              <feather-icon v-else icon="SaveIcon" class="mr-1" />
              <span>{{ isAnySaving ? "Saving..." : "Save All" }}</span>
            </b-button>
          </div>
          </b-card>
        </div>
      </div>
    </div>

    <!-- Export Modal -->
    <b-modal
      v-model="showExportModal"
      title="Export Planner Data"
      @hidden="resetExportForm"
      centered
      no-close-on-backdrop
    >
      <!-- Loading overlay for export modal -->
      <div v-if="exportLoading" class="export-loading-overlay">
        <div class="loading-content">
          <b-spinner
            variant="primary"
            style="width: 3rem; height: 3rem"
            class="mb-3"
          ></b-spinner>
          <h6 class="text-primary mb-2">Generating Export...</h6>
          <p class="text-muted small mb-0">
            Please wait while we prepare your data
          </p>
        </div>
      </div>

      <b-form @submit.prevent :class="{ 'form-disabled': exportLoading }">
        <b-row>
          <b-col md="6">
            <b-form-group label="From Date" label-class="font-weight-bolder">
              <flat-pickr
                v-model="exportForm.fromDate"
                :config="dateConfig"
                class="form-control"
                placeholder="Select from date"
                :disabled="exportLoading"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="To Date" label-class="font-weight-bolder">
              <flat-pickr
                v-model="exportForm.toDate"
                :config="dateConfig"
                class="form-control"
                placeholder="Select to date"
                :disabled="exportLoading"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <b-form-group label="Main Topic" label-class="font-weight-bolder">
              <v-select
                v-model="exportForm.mainTopicIDs"
                :options="exportTopicsOptions"
                label="name"
                :reduce="(val) => val.id"
                placeholder="Select main topics"
                :clearable="false"
                :disabled="exportLoading"
                multiple
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Sub Topic" label-class="font-weight-bolder">
              <v-select
                v-model="exportForm.subTopicIDs"
                :options="exportSubtopicsOptions"
                label="name"
                :reduce="(val) => val.id"
                placeholder="Select sub topics"
                :clearable="false"
                :disabled="exportLoading"
                multiple
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>
      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          @click="showExportModal = false"
          :disabled="exportLoading"
        >
          Cancel
        </b-button>
        <b-button
          variant="success"
          @click="exportPlannerData"
          :disabled="exportLoading"
        >
          <b-spinner v-if="exportLoading" small class="mr-1" />
          {{ exportLoading ? "Generating..." : "Export to Excel" }}
        </b-button>
      </template>
    </b-modal>

    <!-- Import Modal -->
    <b-modal
      v-model="showImportModal"
      title="Import Planner Data"
      @hidden="resetImportForm"
      centered
      no-close-on-backdrop
      size="lg"
    >
      <!-- Loading overlay for import modal -->
      <div v-if="importLoading" class="import-loading-overlay">
        <div class="loading-content">
          <b-spinner
            variant="primary"
            style="width: 3rem; height: 3rem"
            class="mb-3"
          ></b-spinner>
          <h6 class="text-primary mb-2">Processing Import...</h6>
          <p class="text-muted small mb-0">
            Please wait while we update your planner data
          </p>
        </div>
      </div>

      <div :class="{ 'form-disabled': importLoading }">
        <b-form @submit.prevent>
          <b-form-group
            label="Select Excel File"
            label-class="font-weight-bolder"
          >
            <b-form-file
              v-model="importFile"
              :state="Boolean(importFile)"
              placeholder="Choose a file or drop it here..."
              drop-placeholder="Drop file here..."
              accept=".xlsx,.xls"
              :disabled="importLoading"
            ></b-form-file>
          </b-form-group>

          <!-- Preview imported data -->
          <div v-if="importPreviewData.length > 0" class="mt-4">
            <h6 class="text-success mb-2">📊 Preview (First 5 rows):</h6>
            <div
              class="table-responsive"
              style="max-height: 300px; overflow-y: auto"
            >
              <table class="table table-sm table-bordered">
                <thead class="bg-light">
                  <tr>
                    <th>Planner ID</th>
                    <th>Date (Range)</th>
                    <th>From Date</th>
                    <th>To Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, index) in importPreviewData.slice(0, 5)"
                    :key="index"
                    :class="{
                      'table-success': row.valid,
                      'table-warning': !row.valid,
                    }"
                  >
                    <td>{{ row.plannerId || "N/A" }}</td>
                    <td>{{ row.originalDateColumn }}</td>
                    <td>{{ row.fromDate || "N/A" }}</td>
                    <td>{{ row.toDate || "N/A" }}</td>
                    <td>
                      <b-badge :variant="row.valid ? 'success' : 'warning'">
                        {{ row.valid ? "Valid" : "Invalid" }}
                      </b-badge>
                      <small v-if="!row.valid" class="text-muted d-block">{{
                        row.error
                      }}</small>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p class="text-muted small mt-2">
              Total rows: {{ importPreviewData.length }} | Valid:
              {{ importPreviewData.filter((r) => r.valid).length }} | Invalid:
              {{ importPreviewData.filter((r) => !r.valid).length }}
            </p>
          </div>
        </b-form>
      </div>

      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          @click="showImportModal = false"
          :disabled="importLoading"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          @click="processImportFile"
          :disabled="!importFile || importLoading"
          v-if="!importPreviewData.length"
        >
          <feather-icon icon="UploadIcon" class="mr-1" />
          Process File
        </b-button>
        <b-button
          variant="success"
          @click="importPlannerData"
          :disabled="
            importLoading || !importPreviewData.filter((r) => r.valid).length
          "
          v-if="importPreviewData.length"
        >
          <b-spinner v-if="importLoading" small class="mr-1" />
          <feather-icon v-else icon="CheckIcon" class="mr-1" />
          {{
            importLoading
              ? "Importing..."
              : `Import ${
                  importPreviewData.filter((r) => r.valid).length
                } Records`
          }}
        </b-button>
      </template>
    </b-modal>

    <!-- Replicate Modal -->
    <b-modal
      v-model="showReplicateModal"
      title="Replicate Daily Planners"
      centered
      size="lg"
      modal-class="replicate-modal"
      no-close-on-backdrop
      :hide-header-close="replicating"
      @hidden="resetReplicateModal"
    >
      <div v-if="replicating" class="text-center py-4">
        <b-spinner variant="primary" style="width: 3rem; height: 3rem" />
        <p class="mt-3 mb-1 font-weight-bold">
          Replicating {{ selectedSubtopicKeys.length }} subtopic(s)...
        </p>
        <p class="text-muted small mb-0">
          Copying lesson plans from
          <strong>{{ selectedSession || "source session" }}</strong>
          to the selected destination session.
        </p>
      </div>

      <div v-else-if="replicateSummary" class="replicate-results">
        <b-alert :variant="replicateAlertVariant" show class="mb-3">
          <div class="font-weight-bolder mb-50">{{ replicateSummaryTitle }}</div>
          <div class="small">
            From <strong>{{ replicateSummary.sourceSessionName || selectedSession }}</strong>
            to <strong>{{ replicateSummary.sessionName || "—" }}</strong>
          </div>
        </b-alert>

        <b-row class="text-center mb-3">
          <b-col cols="6" md="3" class="mb-1">
            <div class="replicate-stat-box">
              <div class="replicate-stat-value">{{ replicateSummary.total }}</div>
              <div class="replicate-stat-label">Topics</div>
            </div>
          </b-col>
          <b-col cols="6" md="3" class="mb-1">
            <div class="replicate-stat-box replicate-stat-box--success">
              <div class="replicate-stat-value">{{ replicateSummary.replicated }}</div>
              <div class="replicate-stat-label">Plans Copied</div>
            </div>
          </b-col>
          <b-col cols="6" md="3" class="mb-1">
            <div class="replicate-stat-box replicate-stat-box--warning">
              <div class="replicate-stat-value">{{ replicateSummary.skipped }}</div>
              <div class="replicate-stat-label">Skipped</div>
            </div>
          </b-col>
          <b-col cols="6" md="3" class="mb-1">
            <div class="replicate-stat-box replicate-stat-box--danger">
              <div class="replicate-stat-value">{{ replicateSummary.failed }}</div>
              <div class="replicate-stat-label">Failed</div>
            </div>
          </b-col>
        </b-row>

        <b-table
          v-if="replicateResults.length"
          :items="replicateResults"
          :fields="replicateResultFields"
          small
          responsive
          bordered
          show-empty
          empty-text="No result details available"
          class="mb-0"
        >
          <template #cell(status)="data">
            <b-badge :variant="replicateStatusVariant(data.value)">
              {{ data.value }}
            </b-badge>
          </template>
          <template #cell(replicatedCount)="data">
            {{ data.value || 0 }}
          </template>
          <template #cell(message)="data">
            {{ data.value || "—" }}
          </template>
        </b-table>
      </div>

      <div v-else class="replicate-form">
        <p class="mb-2">
          Copy <strong>{{ selectedSubtopicKeys.length }}</strong> selected
          subtopic(s) from
          <strong>{{ selectedSession || "current session" }}</strong>
          into another session.
        </p>
        <b-form-group label="Destination Session" class="mb-0">
          <v-select
            v-model="replicateSessionID"
            :options="replicateSessionOptions"
            label="session"
            :reduce="(val) => val.id"
            :clearable="false"
            placeholder="Select destination session"
            :loading="replicateSessionsLoading"
            class="replicate-session-select"
          />
        </b-form-group>
      </div>

      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          :disabled="replicating"
          @click="showReplicateModal = false"
        >
          {{ replicateSummary ? "Close" : "Cancel" }}
        </b-button>
        <b-button
          v-if="!replicateSummary"
          variant="primary"
          :disabled="!replicateSessionID || replicating"
          @click="replicateDayWisePlanners"
        >
          <feather-icon icon="CopyIcon" size="16" class="mr-50" />
          Replicate
        </b-button>
        <b-button v-else variant="primary" @click="finishReplicate">
          Done
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { required, minLength, between } from "vuelidate/lib/validators";
import { quillEditor } from "vue-quill-editor";
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BImg,
  VBTooltip,
  BFormCheckbox,
  BFormGroup,
  BForm,
  BMediaAside,
  BAside,
  BSpinner,
  BCardTitle,
  BCardText,
  BSidebar,
  VBToggle,
  BInputGroupPrepend,
  BInputGroup,
  BFormTextarea,
  BAvatar,
  BModal,
  BCollapse,
  BIcon,
  BFormFile,
  BAlert,
} from "bootstrap-vue";
import Ripple from "vue-ripple-directive";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import { mapActions, mapGetters } from "vuex";
import { HotTable } from "@handsontable/vue";
import { registerAllModules } from "handsontable/registry";
import "handsontable/dist/handsontable.full.css";
import "handsontable/dist/handsontable.min.css";
import "handsontable/styles/ht-theme-main.min.css";
import * as XLSX from "xlsx";
import axios from "axios";
import teacherPlannerScopeMixin from "../planner/teacherPlannerScopeMixin";

// register Handsontable's modules
registerAllModules();

export default {
  mixins: [teacherPlannerScopeMixin],
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BFormGroup,
    BFormInput,
    BSpinner,
    vSelect,
    flatPickr,
    HotTable,
    BIcon,
    BBadge,
    BModal,
    BFormFile,
    BFormCheckbox,
    BAlert,
  },
  props: {},
  directives: {
    "b-tooltip": VBTooltip,
    Ripple,
  },
  async created() {
    // Ensure rights are initialized like other planner pages
    let rightMap = {};
    this.$store.state.menu.forEach((el) => {
      el.children.forEach((ch) => {
        rightMap[ch.route] = {
          view: ch.view,
          add: ch.add,
          edit: ch.edit,
          delete: ch.delete,
          assignSections: ch.assignSections,
        };
      });
    });
    this.$store.commit("setRights", rightMap);

    // Resolve rights using the current route name
    const pageRights = this.$store.state.rights[this.$route.name];
    if (!pageRights || !pageRights.view) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    this.rights = pageRights;

    // Initialize basic object structure
    this.myObj = {
      id: 0,
      clsID: 0,
      secID: 0,
      sessionID: null,
      subjectID: 0,
      tID: 0,
      campusID: this.$store.state.userData.cId,
    };
    this.secIDs = null;
    await this.LoadTeacher();
  },
  computed: {
    ...mapGetters({ cmbDepartment: "cmbDepartment" }),
    ...mapGetters({ data: "data" }),

    canFilter() {
      return this.myObj.clsID && this.secIDs;
    },

    canReplicate() {
      return !!(this.rights && this.rights.add && this.rights.edit);
    },

    subtopicFields() {
      const fields = [
        {
          key: "sno",
          label: "S.No",
          thClass: "text-center",
          tdClass: "text-center",
          thStyle: { width: "70px" },
        },
        { key: "mainTopicName", label: "Topic" },
        {
          key: "name",
          label: "Subtopic Name",
          thStyle: { minWidth: "220px" },
          tdClass: "subtopic-cell",
        },
        {
          key: "status",
          label: "Status",
          thStyle: { width: "140px", textAlign: "center" },
          tdClass: "text-center",
        },
        {
          key: "actions",
          label: "Actions",
          thStyle: { width: "220px", textAlign: "center" },
          tdClass: "actions-cell text-center",
        },
      ];

      if (this.canReplicate) {
        return [
          {
            key: "selected",
            label: "",
            thClass: "text-center",
            tdClass: "text-center",
            thStyle: { width: "48px" },
          },
          ...fields,
        ];
      }

      return fields;
    },

    replicableSubtopics() {
      return (this.subtopics || []).filter((item) =>
        this.hasSavedDailyPlanner(item)
      );
    },

    canOpenReplicate() {
      return (
        this.selectedSubtopicKeys.length > 0 &&
        this.myObj.clsID &&
        this.secIDs !== null &&
        this.secIDs !== undefined &&
        this.myObj.subjectID &&
        this.myObj.sessionID
      );
    },

    replicateSessionOptions() {
      return (this.sessions || []).filter(
        (session) => session.id !== this.myObj.sessionID
      );
    },

    isAllSubtopicsSelected() {
      if (!this.replicableSubtopics.length) return false;
      return this.replicableSubtopics.every((item) =>
        this.isSubtopicSelected(item)
      );
    },

    isSubtopicsIndeterminate() {
      const selectedCount = this.replicableSubtopics.filter((item) =>
        this.isSubtopicSelected(item)
      ).length;
      return (
        selectedCount > 0 && selectedCount < this.replicableSubtopics.length
      );
    },

    replicateAlertVariant() {
      if (!this.replicateSummary) return "info";
      if (this.replicateSummary.failed > 0) return "danger";
      if (this.replicateSummary.skipped > 0) return "warning";
      return "success";
    },

    replicateSummaryTitle() {
      if (!this.replicateSummary) return "";
      const { replicated, skipped, failed } = this.replicateSummary;
      if (failed > 0) {
        return "Replication completed with some failures.";
      }
      if (skipped > 0 && replicated > 0) {
        return "Replication completed with some subtopics skipped.";
      }
      if (skipped > 0 && replicated === 0) {
        return "All selected subtopics were already in the destination session.";
      }
      return "All selected planners replicated successfully.";
    },

    filters: function () {
      return this.items.filter((pro) => {
        return pro.subject.toLowerCase().match(this.searchQuery.toLowerCase());
      });
    },

    selectedGrade() {
      if (!this.myObj.clsID || !this.classesOptions.length) return "";
      const selectedClass = this.classesOptions.find(
        (cls) => cls.id === this.myObj.clsID
      );
      return selectedClass ? selectedClass.name : "";
    },

    selectedSubject() {
      if (!this.myObj.subjectID || !this.subjects.length) return "";
      const selectedSubject = this.subjects.find(
        (sub) => sub.id === this.myObj.subjectID
      );
      return selectedSubject ? selectedSubject.subject : "";
    },

    selectedSection() {
      if (
        this.secIDs === null ||
        this.secIDs === undefined ||
        !this.currentSections.length
      )
        return "";
      const selectedSection = this.currentSections.find(
        (sec) => sec.id === this.secIDs
      );
      return selectedSection ? selectedSection.section : "";
    },

    plannerCampusID() {
      return this.reportDomain === "myskooliuss"
        ? 1
        : this.$store.state.userData.cId;
    },

    selectedSession() {
      if (!this.myObj.sessionID || !this.sessions.length) return "";
      const selectedSession = this.sessions.find(
        (session) => session.id === this.myObj.sessionID
      );
      return selectedSession ? selectedSession.session : "";
    },

    selectedTopic() {
      if (!this.filterObj.topicID || !this.topics.length) return "";
      const selectedTopic = this.topics.find(
        (topic) => topic.id === this.filterObj.topicID
      );
      return selectedTopic ? selectedTopic.name : "";
    },

    selectedSubtopic() {
      if (!this.filterObj.subtopicID || !this.subtopics.length) return "";
      const selectedSubtopic = this.subtopics.find(
        (subtopic) => subtopic.id === this.filterObj.subtopicID
      );
      return selectedSubtopic ? selectedSubtopic.name : "";
    },

    currentSelectedTopic() {
      // Show the actual selected topic name or first available topic
      if (this.filterObj.topicID && this.topics.length > 0) {
        const topic = this.topics.find((t) => t.id === this.filterObj.topicID);
        return topic ? topic.name : "Topic not found";
      }

      // If no topic is specifically selected, show the first available topic
      if (this.topics.length > 0) {
        return this.topics[0].name;
      }

      return "Select subject to load topics";
    },

    currentSelectedSubtopic() {
      // Show the actual selected subtopic name or helpful message
      if (this.filterObj.subtopicID && this.subtopics.length > 0) {
        const subtopic = this.subtopics.find(
          (s) => s.id === this.filterObj.subtopicID
        );
        return subtopic ? subtopic.name : "Subtopic not found";
      }

      // If subtopics are available but none selected
      if (this.subtopics.length > 0) {
        return `${this.subtopics.length} subtopics available`;
      }

      return "No subtopics available";
    },

    // Computed properties for filter dropdowns
    topicFilterOptions() {
      // Return topics in the format expected by v-select
      return this.topics.map((topic) => ({
        id: topic.id,
        name: topic.name,
      }));
    },

    subtopicFilterOptions() {
      // Return subtopics filtered by selected topic
      if (!this.filterObj.topicID) {
        return [];
      }
      return this.allSubtopics
        .filter((subtopic) => subtopic.mainTopicID === this.filterObj.topicID)
        .map((subtopic) => ({
          id: subtopic.id,
          name: subtopic.name,
        }));
    },

    // Computed property to check if print is available
    canPrint() {
      return (
        this.myObj.clsID &&
        this.secIDs !== null &&
        this.secIDs !== undefined &&
        this.myObj.sessionID &&
        this.myObj.subjectID
      );
    },

    // Computed property to check if export is available
    canExportData() {
      // Export should be enabled when:
      // 1. Initial data loading is complete
      // 2. Either subtopics are available OR daily planner forms exist
      // 3. Not currently in other loading state
      return (
        !this.dataLoading &&
        !this.otherLoading &&
        (this.subtopics.length > 0 || this.dailyPlannerForms.length > 0)
      );
    },

    // Check if any form is currently saving
    isAnySaving() {
      return this.dailyPlannerForms.some((form) => form.saving);
    },

    // Dynamic hotSettings with proper Vue component context
    dynamicHotSettings() {
      const vueComponent = this; // Store reference to Vue component

      return {
        licenseKey: "non-commercial-and-evaluation",
        minSpareRows: 5, // Always keep 5 spare rows available for user input
        stretchH: "all",
        width: "100%",
        height: 400,
        rowHeights: 100,
        colWidths: [200, 200, 200, 200],
        contextMenu: true,
        // Enable copy-paste functionality
        copyPaste: true,
        // Allow pasting into multiple cells
        allowInsertRow: true,
        allowInsertColumn: false,
        // Handle different paste modes
        copyPasteMode: "cells",
        // Enable proper paste from external sources
        outsideClickDeselects: false,
        // Enable proper handling of tab-separated values (from Word tables)
        persistentState: true,
        // Custom paste parser for better Word table support
        beforeCopy: (data, coords) => {
        },
        // Enhanced paste handling
        beforePaste: (data, coords) => {

          // Process the data to handle tab-separated and multi-line content
          const processedData = data.map((row) => {
            if (
              Array.isArray(row) &&
              row.length === 1 &&
              typeof row[0] === "string"
            ) {
              // If we have a single string that might contain tabs or other separators
              let cellContent = row[0];

              // Clean up the content first
              cellContent = cellContent
                .replace(/\r\n/g, "\n")
                .replace(/\r/g, "\n");

              // Try to split by tabs first (most common from Word tables)
              if (cellContent.includes("\t")) {
                const splitByTabs = cellContent
                  .split("\t")
                  .map((cell) => cell.trim());
                // Ensure we have exactly 4 columns
                const result = splitByTabs.slice(0, 4);
                while (result.length < 4) {
                  result.push("");
                }
                return result;
              }

              // Try to detect patterns that suggest table data
              // Look for patterns like "Text | Text | Text | Text"
              if (cellContent.includes("|")) {
                const splitByPipe = cellContent
                  .split("|")
                  .map((cell) => cell.trim());
                if (splitByPipe.length >= 2) {
                  const result = splitByPipe.slice(0, 4);
                  while (result.length < 4) {
                    result.push("");
                  }
                  return result;
                }
              }

              // Try to split by multiple spaces (fallback for space-separated data)
              const spacePattern = /\s{3,}/; // 3 or more spaces
              if (spacePattern.test(cellContent)) {
                const splitBySpaces = cellContent
                  .split(spacePattern)
                  .map((cell) => cell.trim())
                  .filter((cell) => cell);
                if (splitBySpaces.length > 1) {
                  const result = splitBySpaces.slice(0, 4);
                  while (result.length < 4) {
                    result.push("");
                  }
                  return result;
                }
              }

              // If no clear separators, put the content in the first column
              return [cellContent.trim(), "", "", ""];
            }

            // If it's already properly formatted, ensure it has 4 columns
            if (Array.isArray(row)) {
              const result = row
                .slice(0, 4)
                .map((cell) => (cell ? cell.toString().trim() : ""));
              while (result.length < 4) {
                result.push("");
              }
              return result;
            }

            return ["", "", "", ""];
          });

          // Replace the original data with processed data
          data.splice(0, data.length, ...processedData);

          return true;
        },
        afterPaste: function (data, coords) {

          // Set flag to prevent regular cleanup during paste processing
          vueComponent.isPasting = true;

          // Calculate how many rows and columns were pasted
          const rowCount = data.length;
          const filledCells = data.reduce((count, row) => {
            return (
              count +
              row.filter((cell) => cell && cell.toString().trim()).length
            );
          }, 0);

          // Automatically clean up empty rows after paste with delay
          if (
            vueComponent &&
            typeof vueComponent.cleanupEmptyRowsAfterPaste === "function"
          ) {
            setTimeout(() => {
              vueComponent.cleanupEmptyRowsAfterPaste();
              // Reset the flag after cleanup is complete - extended delay to prevent interference
              setTimeout(() => {
                vueComponent.isPasting = false;
              }, 500); // Increased from 100ms to 500ms
            }, 200);
          }

          // Show success feedback to user if data was actually pasted
          if (rowCount > 0 && filledCells > 0) {
            setTimeout(() => {
              if (vueComponent && vueComponent.$bvToast) {
                // vueComponent.$bvToast.toast(`Successfully pasted ${rowCount} rows with ${filledCells} cells of data`, {
                //   title: "Paste Successful",
                //   variant: "success",
                //   solid: true,
                // });
              }
            }, 800); // Also increased timing to avoid conflicts
          }
        },
        afterChange: function (changes, source) {
          if (source === "loadData") return;

          // Don't clean up during paste operations or immediately after paste
          if (
            source !== "paste" &&
            !vueComponent.isPasting &&
            vueComponent &&
            typeof vueComponent.cleanupEmptyRows === "function"
          ) {
            setTimeout(() => {
              // Double-check that we're not still in paste mode with longer delay
              if (!vueComponent.isPasting) {
                vueComponent.cleanupEmptyRows();
              } else {
              }
            }, 600); // Increased from 150ms to 600ms to avoid interference
          }
        },
        // Cell rendering for better display
        cells: (row, col) => {
          const cellProperties = {};
          // Make cells taller and handle word wrap
          cellProperties.renderer = function (
            instance,
            td,
            row,
            col,
            prop,
            value,
            cellProperties
          ) {
            td.innerHTML = value || "";
            td.style.whiteSpace = "pre-wrap";
            td.style.overflow = "hidden";
            td.style.wordWrap = "break-word";
            td.style.verticalAlign = "top";
            td.style.padding = "8px";
            return td;
          };
          return cellProperties;
        },
        // Handle keyboard navigation properly
        enterBeginsEditing: true,
        enterMoves: { row: 1, col: 0 },
        tabMoves: { row: 0, col: 1 },
        // Enable proper text editing
        wordWrap: true,
        autoWrapRow: true,
        autoWrapCol: true,
      };
    },
    exportTopicsOptions() {
      const allOption = { id: 0, name: "All Topics" };
      const topicOptions = this.exportTopics.map((topic) => ({
        id: topic.id,
        name: topic.topic || topic.mainTopic || "Unknown Topic",
      }));
      return [allOption, ...topicOptions];
    },

    exportSubtopicsOptions() {
      const allOption = { id: 0, name: "All Subtopics" };

      // Filter subtopics based on selected main topics
      let filteredSubtopics = this.exportSubtopics;

      if (
        this.exportForm.mainTopicIDs &&
        this.exportForm.mainTopicIDs.length > 0 &&
        !this.exportForm.mainTopicIDs.includes(0)
      ) {
        // If specific topics are selected, filter subtopics to match those topics
        filteredSubtopics = this.exportSubtopics.filter((subtopic) =>
          this.exportForm.mainTopicIDs.includes(subtopic.mainTopicID)
        );

      }

      const subtopicOptions = filteredSubtopics.map((subtopic) => ({
        id: subtopic.id,
        name: subtopic.subTopic || "Unknown Subtopic",
      }));

      return [allOption, ...subtopicOptions];
    },
  },
  data() {
    return {
      editorOptions: [
        ["bold", "italic", "underline", "strike"], // toggled buttons
        ["blockquote"],

        [{ header: 1 }, { header: 2 }], // custom button values
        [{ list: "ordered" }, { list: "bullet" }],
        [{ script: "sub" }, { script: "super" }], // superscript/subscript
        [{ indent: "-1" }, { indent: "+1" }], // outdent/indent
        [{ direction: "rtl" }], // text direction

        [{ size: ["small", false, "large", "huge"] }], // custom dropdown
        [{ header: [1, 2, 3, 4, 5, 6, false] }],

        [{ color: [] }, { background: [] }], // dropdown with defaults from theme

        [{ align: [] }],
      ],
      collapseData: [{ state: true }],
      teacherData: [],
      isOther: false,
      rights: {},
      myDepartments: [],
      request: false,
      colorDep: [],
      visibility1: false,
      reportDomain: this.$store.state.report.replace(/_/g, ""),

      sidebarTitle: "Add Subject",
      sidebarButton: "Save",
      buttonIcon: "PlusIcon",
      dataLoading: false,

      visibility: false,
      otherLoading: false,
      searchQuery: "",
      currentPage: 1,
      perPage: 30,
      totalRows: 0,

      errors: {
        status: false,
      },

      index: null,

      fields: [
        { label: "Subject", key: "subject" },
        { key: "actions", label: "actions" },
      ],
      config: {
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d",
        disable: [],
      },

      items: [],
      classes: [],
      classesOptions: [],
      allClasses: [],
      staff: [],
      staffObj: [],
      currentSections: [],
      sessions: [],
      subjects: [],
      currIDs: [],
      secIDs: null,
      myObj: {
        clsID: 0,
        secID: 0,
        sessionID: null,
        subjectID: 0,
        tID: 0,
        campusID: this.$store.state.userData.cId,
      },
      typeObj: {
        id: 0,
        questionType: "",
        campusID: this.$store.state.userData.cId,
      },
      quesTypes: [],
      visibility4: false,
      typeLoading: false,
      typeFields: [
        { label: "type", key: "questionType" },
        { key: "actions", label: "actions" },
      ],
      chapObj: {
        id: 0,
        chapter: "",
        subjectID: 0,
        campusID: this.$store.state.userData.cId,
      },
      chapters: [],
      visibility3: false,
      chapLoading: false,
      chFields: [
        { label: "chapter", key: "chapter" },
        { key: "actions", label: "actions" },
      ],
      categories: [],

      savingSess: false,
      sessFields: [
        { label: "Name", key: "session" },
        { key: "actions", label: "actions" },
      ],

      coverLoading: false,
      expLoading: false,
      catObj: {
        id: 0,
        category: "",
        campusID: this.$store.state.userData.cId,
      },
      visibility2: false,
      savingCat: false,
      catFields: [
        { label: "level", key: "category" },
        { key: "actions", label: "actions" },
      ],
      days: [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ],
      showPlannerData: false,
      plannerData: {},
      plannerTableData: [
        ["", "", "", ""],
        ["", "", "", ""],
        ["", "", "", ""],
        ["", "", "", ""],
        ["", "", "", ""],
      ],
      plannerFields: [
        { key: "lessonOrganisation", label: "Lesson Organisation" },
        { key: "activity", label: "Activity" },
        { key: "resource", label: "Resource" },
        { key: "assessment", label: "Assessment" },
        { key: "actions", label: "Actions" },
      ],
      filterObj: {
        topicID: null,
        subtopicID: null,
      },
      topics: [],
      subtopics: [],
      allSubtopics: [],
      showDailyPlanTable: false,
      dailyPlannerViewMode: false,
      selectedSubtopicForPlan: "",
      subtopicPlannerMap: {},
      // currentSubtopicID -> { mainTopicID, subtopicID } used by LoadSelected
      // when replicated plans still point at source-session topic IDs
      subtopicPlannerRemap: {},
      topicMetaCacheBySession: {},
      selectedSubtopicKeys: [],
      showReplicateModal: false,
      replicateSessionID: null,
      replicateSessionsLoading: false,
      replicating: false,
      replicateSummary: null,
      replicateResults: [],
      replicateResultFields: [
        {
          key: "mainTopicID",
          label: "Topic ID",
          thClass: "text-center",
          tdClass: "text-center",
        },
        {
          key: "subtopicID",
          label: "Subtopic ID",
          thClass: "text-center",
          tdClass: "text-center",
        },
        { key: "status", label: "Status", thClass: "text-center", tdClass: "text-center" },
        {
          key: "replicatedCount",
          label: "Copied",
          thClass: "text-center",
          tdClass: "text-center",
        },
        { key: "message", label: "Message" },
      ],
      dailyPlannerFields: [
        {
          key: "lessonOrganisation",
          label: "Lesson Organisation",
          thClass: "w-25",
        },
        { key: "activity", label: "Activity", thClass: "w-25" },
        { key: "resource", label: "Resource", thClass: "w-25" },
        {
          key: "assessmentStrategy",
          label: "Assessment Strategy",
          thClass: "w-25",
        },
      ],
      dailyPlannerData: {
        dtFrom: "",
        dayFromName: "",
        dtTo: "",
        dayToName: "",
        duration: "",
      },
      dateConfig: {
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d",
        disable: [],
      },
      savedPlanners: [],
      savedPlannerFields: [
        { key: "lessonDetails", label: "Lesson Details" },
        {
          key: "actions",
          label: "Actions",
          thClass: "text-center",
          tdClass: "text-center",
        },
      ],
      currentEditingPlannerId: null,
      colHeaders: [
        "Lesson Organisation",
        "Activity",
        "Resource",
        "Assessment Strategy",
      ],
      columns: [
        {
          data: "lessonOrganisation",
          type: "text",
          width: 200,
        },
        {
          data: "activity",
          type: "text",
          width: 200,
        },
        {
          data: "resource",
          type: "text",
          width: 200,
        },
        {
          data: "assessmentStrategy",
          type: "text",
          width: 200,
        },
      ],
      // Store Vue component reference for HandsOnTable callbacks
      vueComponentRef: null,
      // Flag to prevent cleanup during paste operations
      isPasting: false,
      isLoadingPlannerData: false,
      originalPlannerDetails: [],
      dailyPlannerForms: [],
      // Export modal data
      showExportModal: false,
      exportLoading: false,
      exportForm: {
        fromDate: "",
        toDate: "",
        mainTopicIDs: [],
        subTopicIDs: [],
      },
      exportTopics: [],
      exportSubtopics: [],
      // Import modal data
      showImportModal: false,
      importLoading: false,
      importFile: null,
      importPreviewData: [],
      // Track which forms are being deleted
      deletingForms: [],
      // Global column layout for all forms - default to English mode (4 columns)
      globalColumnLayout: 4,
    };
  },
  validations: {
    depObj: {
      name: {
        required,
        minLength: minLength(4),
      },
    },
    age: {
      between: between(20, 30),
    },
  },
  methods: {
    ...mapActions({ getDepartment: "getDepartment" }),
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    // Debug method to check and force section selection
    debugSectionSelection() {

      // Force selection to "All" if we have sections but no selection
      if (
        this.currentSections.length > 0 &&
        (this.secIDs === null || this.secIDs === undefined)
      ) {
        this.secIDs = 0;
        this.$forceUpdate();
      }
    },

    setSec() {
      if (this.secIDs.at(-1) == 0) {
        this.secIDs = [0];
      } else {
        this.secIDs = this.secIDs.filter((el) => el != 0);
      }
    },

    FillSections(text) {

      // Reset sections and related data
      this.currentSections = [];

      if (text !== "filled") {
        this.secIDs = null; // Set to null first to clear selection
        this.myObj.subjectID = 0;
        this.topics = [];
        this.filterObj.topicID = null;
        this.subtopics = [];
        this.filterObj.subtopicID = null;
      }

      if (this.myObj.clsID) {
        const classData = this.classes.find(
          (elem) => elem.cls.id === this.myObj.clsID
        );

        if (classData && classData.sections.length > 0) {
          // Add "All" option as the first item with ID 0
          this.currentSections = [
            { id: 0, section: "All" },
            ...classData.sections,
          ];

        } else {
          // If no sections found, set "All" as default
          this.currentSections = [{ id: 0, section: "All" }];
        }

        // Force Vue to update the DOM and v-select options first
        this.$forceUpdate();

        // Use multiple $nextTick to ensure the v-select component is fully updated
        this.$nextTick(() => {

          this.$nextTick(() => {
            // Set secIDs to 0 (All) by default after sections are populated and component is updated
            this.secIDs = 0;

            this.$nextTick(() => {

              // Force another update to ensure the selection is visible
              this.$forceUpdate();

              // Add a timeout fallback to ensure selection happens
              setTimeout(() => {
                this.debugSectionSelection();

                // Final fallback - if still not selected, force it
                if (this.secIDs !== 0 && this.currentSections.length > 0) {
                  this.secIDs = 0;
                  this.$forceUpdate();
                }
              }, 500);
            });
          });
        });
      }

      if (text === "filled") this.fillSubjects("filled");
      else if (this.isTeacherScoped && this.myObj.clsID) {
        this.subjects = this.getAssignedSubjectsForClass(
          this.myObj.clsID,
          this.secIDs ?? 0
        );
      }
    },

    fillSubjects(text) {
      if (text !== "filled") {
        this.myObj.subjectID = 0;
        this.topics = [];
        this.filterObj.topicID = null;
        this.subtopics = [];
        this.filterObj.subtopicID = null;
        // Reset loading state and hide planner data when subject is cleared
        this.otherLoading = false;
        this.showPlannerData = false;
      }

      if (this.isTeacherScoped && this.myObj.clsID) {
        this.subjects = this.getAssignedSubjectsForClass(
          this.myObj.clsID,
          this.secIDs
        );
      }
    },

    getClassName(id) {
      let obj = this.classesOptions.find((el) => el.id == id);
      return obj ? obj.name : "-";
    },

    getSubName(id) {
      let obj = this.subjects.find((el) => el.id == id);
      return obj ? obj.subject : "-";
    },

    getSecName(clsID, id) {
      if (id === 0) return "All";
      let obj = this.classes.find((el) => el.cls.id == clsID);
      if (obj) {
        let sec = obj.sections.find((el) => el.id == id);
        return sec ? sec.section : "-";
      } else return "-";
    },

    async checkData() {
      // This method is no longer needed
    },

    CheckCatName() {
      var elem = this.$refs["cat_name"];
      if (this.catObj.category.trim() == "") {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    async AddCategory() {
      this.CheckCatName();
      if (this.CheckCatName() == true) {
        if (this.catObj.id == 0) {
          //Add
          this.savingCat = true;
          var status = await this.post({
            url:
              this.$store.state.domain +
              "QuizCategory?db=" +
              this.$store.state.userData.db,
            body: this.catObj,
            message: "Quiz Level added successfully.",
            context: this,
            token: this.$store.state.userData.token,
          });
          this.savingCat = false;
          if (status) {
            this.loadCat();
            this.catObj = {
              id: 0,
              category: "",
              campusID: this.$store.state.userData.cId,
            };
            this.buttonIcon = "PlusIcon";
          }
        } else {
          //Edit
          this.savingCat = true;
          var status = await this.put({
            url:
              this.$store.state.domain +
              "QuizCategory/" +
              this.catObj.id +
              "?db=" +
              this.$store.state.userData.db,
            body: this.catObj,
            message: "Quiz Level updated successfully.",
            context: this,
            token: this.$store.state.userData.token,
          });
          this.savingCat = false;
          if (status) {
            this.loadCat();
            this.catObj = {
              id: 0,
              category: "",
              campusID: this.$store.state.userData.cId,
            };
            this.buttonIcon = "PlusIcon";
          }
        }
      }
    },
    editCat(item) {
      this.catObj = { ...item };
      this.buttonIcon = "Edit2Icon";
      var elem = this.$refs["cat_name"];
      elem.state = undefined;
    },
    openCat() {
      this.catObj = {
        id: 0,
        category: "",
        campusID: this.$store.state.userData.cId,
      };

      this.visibility2 = true;
      this.buttonIcon = "PlusIcon";

      var elem = this.$refs["cat_name"];
      elem.state = undefined;
    },
    async DeleteCat(id) {
      let result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: this.$t("Yes, Delete it!"),
      });

      if (result.isConfirmed) {
        var status = await this.delete({
          url:
            this.$store.state.domain +
            "QuizCategory/" +
            id +
            "?db=" +
            this.$store.state.userData.db,
          message: "Quiz Level removed successfully.",
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) this.loadCat();
      }
    },

    setSubjects() {
      this.currIDs = this.syllObj.reduce((acc, el) => {
        acc.push(el.subjectID);
        return acc;
      }, []);
    },
    AddValue(item, ind) {
      if (
        item.dateFrom &&
        item.dateTo &&
        item.topic !== "" &&
        item.lesson.trim() !== ""
      ) {
        this.collapseData[ind].state = false;
        this.collapseData.push({ state: true });
        this.syllObj.push({
          id: 0,
          tID: this.$route.params.id,
          plannerGridID: 0,
          dateFrom: null,
          dateTo: null,
          range: "",
          topic: "",
          materials: "",
          objectives: "",
          lesson: "",
          campusID: this.$store.state.userData.cId,
        });
        // setTimeout(() => {
        //   let elem = document.getElementById("option" + ind);
        //   elem.focus();
        //   elem.select();
        // }, 100);
      } else {
        this.$bvToast.toast(this.$t("Enter the details correctly!"), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      }
    },
    async removeValue(item, ind) {
      if (item.id == 0) {
        this.collapseData.splice(ind, 1);
        this.syllObj.splice(ind, 1);
      } else {
        var status = await this.delete({
          url:
            this.$store.state.domain +
            "PlannerData/" +
            item.id +
            "?db=" +
            this.$store.state.userData.db,
          grading: true,
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) {
          this.collapseData.splice(ind, 1);
          this.syllObj.splice(ind, 1);
        }
      }
    },
    async Edit(id) {
      var obj = {
        url:
          this.$store.state.domain +
          "Subjects/GetSelected?id=" +
          id +
          "&db=" +
          this.$store.state.userData.db,
        token: this.$store.state.userData.token,
      };
      this.myObj = await this.get(obj);
      this.adding = false;
      this.visibility = true;
      this.sidebarTitle = "Edit Subject";
      this.sidebarButton = "Update";
      var elem = this.$refs["subject"];
      elem.state = undefined;
    },

    hideSideBar() {
      this.visibility = false;
    },
    async LoadTeacher() {
      await this.initTeacherPlannerScope();

      if (this.isTeacherScoped) {
        await this.loadTeacherClasses();
        this.classes = this.normalizeTeacherClassesToLoadFormat();
        this.classesOptions = this.classes.map((el) => el.cls);
        this.myObj.tID = this.teacherScopeTID;
      } else {
        var obj = {
          url:
            this.$store.state.domain +
            "myclasses/LoadClasses?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&dID=0",
          token: this.$store.state.userData.token,
        };
        this.classes = await this.get(obj);
        this.classesOptions = this.classes.map((el) => el.cls);
      }

      if (!this.isTeacherScoped) {
        var obj2 = {
          url:
            this.$store.state.domain +
            "Subjects?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        };
        this.subjects = await this.get(obj2);
      } else {
        this.subjects = [];
      }

      await this.loadSession();

      if (this.$route.params.id > 0) this.FillSections("filled");
    },
    async LoadData() {
      this.dataLoading = true;
      let obj = {
        url:
          this.$store.state.domain +
          "PlannerData/LoadData?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.$store.state.userData.cId +
          "&tID=" +
          this.myObj.tID +
          "&gridID=" +
          this.$route.params.id +
          "&clsID=" +
          this.myObj.clsID +
          "&secID=" +
          this.myObj.secID +
          "&subID=" +
          this.myObj.subjectID,
        token: this.$store.state.userData.token,
      };
      this.syllObj = await this.get(obj);

      this.collapseData = this.syllObj.map((el) => ({ state: false }));
      this.syllObj = this.syllObj.map((el) => ({
        ...el,
        range: `${el.dateFrom} to ${el.dateTo}`,
      }));

      this.dataLoading = false;

      // let st = {
      //   url:
      //     this.$store.state.domain +
      //     "staff/filter?db=" +
      //     this.$store.state.userData.db +
      //     "&cId=" +
      //     this.$store.state.userData.cId +
      //     "&dptID=0&status=present",
      //   token: this.$store.state.userData.token,
      // };
      // this.staff = await this.get(st);

      // var obj2 = {
      //   url:
      //     this.$store.state.domain +
      //     "myclasses/LoadClasses?db=" +
      //     this.$store.state.userData.db +
      //     "&cID=" +
      //     this.$store.state.userData.cId +
      //     "&dID=0",
      //   token: this.$store.state.userData.token,
      // };
      // this.allClasses = await this.get(obj2);
      // this.allClasses.forEach((el) => this.classes.push(el.cls));
      // if (this.$route.params.id > 0) this.FillSections("filled");

      // var obj = {
      //   url:
      //     this.$store.state.domain +
      //     "Subjects?db=" +
      //     this.$store.state.userData.db +
      //     "&cId=" +
      //     this.$store.state.userData.cId,
      //   token: this.$store.state.userData.token,
      // };
      // this.subjects = await this.get(obj);
      // if (this.$route.params.id > 0) this.setSubjects();

    },

    async loadQues() {
      var obj = {
        url:
          this.$store.state.domain +
          "QuizQuestions/LoadQuestion?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId +
          "&qID=" +
          this.$route.params.id,
        token: this.$store.state.userData.token,
      };

      this.myObj = await this.get(obj);
      this.syllObj = this.myObj.syllabus;
    },

    async loadCat() {
      var obj = {
        url:
          this.$store.state.domain +
          "QuizCategory?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      this.categories = await this.get(obj);

    },
    async loadSession() {
      try {
        const url = `${this.$store.state.domain}Sessions?db=${this.$store.state.userData.db}&cID=${this.plannerCampusID}`;
        const res = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
          },
        });

        if (
          res.data &&
          res.data.status === "success" &&
          Array.isArray(res.data.data)
        ) {
          this.sessions = res.data.data;
        } else if (Array.isArray(res.data?.data)) {
          this.sessions = res.data.data;
        } else if (Array.isArray(res.data)) {
          this.sessions = res.data;
        } else {
          this.sessions = [];
        }

        if (!this.myObj.sessionID && this.sessions.length) {
          const currentSession = this.sessions.find(
            (el) => el.isCurrentSession === true
          );
          this.myObj.sessionID = currentSession
            ? currentSession.id
            : this.sessions[this.sessions.length - 1].id;
        }
      } catch (error) {
        console.error("Error loading sessions:", error);
        this.sessions = [];
      }
    },

    onSessionChange(sessionID) {
      if (sessionID !== undefined && sessionID !== null && sessionID !== "") {
        this.myObj.sessionID = sessionID;
      }
      this.resetPlannerSheetData();
      this.subtopicPlannerMap = {};
      this.subtopicPlannerRemap = {};
      this.clearSubtopicSelection();
      if (this.showDailyPlanTable) {
        this.dailyPlannerForms = [];
        this.loadDailyPlannerData();
      }
      if (this.myObj.subjectID) {
        this.loadTopics();
      } else {
        this.loadSavedPlanners();
      }
    },

    resolvePlannerSectionID() {
      const section = this.secIDs;
      if (section === null || section === undefined || section === "") return 0;
      if (Array.isArray(section)) {
        if (!section.length || section.includes(0) || section.includes("0")) {
          return 0;
        }
        const first = Number(section[0]);
        return Number.isNaN(first) ? 0 : first;
      }
      const n = Number(section);
      return Number.isNaN(n) ? 0 : n;
    },

    buildPlannerDayWiseLoadUrl(mainTopicID = 0, subtopicID = 0) {
      return (
        this.$store.state.domain +
        "PlannerDayWise/LoadSelected?db=" +
        this.$store.state.userData.db +
        "&campusID=" +
        this.plannerCampusID +
        "&classID=" +
        (this.myObj.clsID || 0) +
        "&sectionID=" +
        this.resolvePlannerSectionID() +
        "&subjectID=" +
        (this.myObj.subjectID || 0) +
        "&sessionID=" +
        (this.myObj.sessionID || 0) +
        "&mainTopicID=" +
        (mainTopicID || 0) +
        "&subtopicID=" +
        (subtopicID || 0) +
        "&fromPublished=false"
      );
    },

    async fetchPlannerDayWise(mainTopicID = 0, subtopicID = 0) {
      try {
        // Use axios directly so we always get the raw API body
        // (vuex `get` + returnData can return odd shapes).
        const response = await axios.get(
          this.buildPlannerDayWiseLoadUrl(mainTopicID, subtopicID),
          {
            headers: {
              Authorization: `bearer ${this.$store.state.userData.token}`,
            },
          }
        );
        return this.normalizePlannerDayWiseResponse(response.data);
      } catch (error) {
        console.error("PlannerDayWise LoadSelected failed:", error);
        return { success: false, entries: [], raw: null };
      }
    },

    normalizePlannerDayWiseResponse(response) {
      if (response == null || response === false) {
        return { success: false, entries: [], raw: null };
      }

      if (typeof response === "string") {
        const trimmed = response.trim();
        if (!trimmed || trimmed.toLowerCase().includes("no data")) {
          return { success: false, entries: [], raw: response };
        }
        try {
          return this.normalizePlannerDayWiseResponse(JSON.parse(trimmed));
        } catch (e) {
          return { success: false, entries: [], raw: response };
        }
      }

      if (response.status === "error") {
        return { success: false, entries: [], raw: response.data || null };
      }

      if (response.status === "success") {
        if (response.data === undefined) {
          return { success: true, entries: [], raw: response };
        }
        return this.normalizePlannerDayWiseResponse(response.data);
      }

      if (Array.isArray(response)) {
        return { success: true, entries: response, raw: response };
      }

      if (response.plannerDayWise || response.plannerDayWiseDetails) {
        return { success: true, entries: [response], raw: response };
      }

      if (response.id && (response.subtopicID || response.dtFrom)) {
        return { success: true, entries: [response], raw: response };
      }

      if (response.data !== undefined) {
        return this.normalizePlannerDayWiseResponse(response.data);
      }

      if (Array.isArray(response.items)) {
        return { success: true, entries: response.items, raw: response.items };
      }

      if (Array.isArray(response.list)) {
        return { success: true, entries: response.list, raw: response.list };
      }

      return { success: false, entries: [], raw: response };
    },

    extractPlannerEntry(entry) {
      if (!entry || typeof entry !== "object") {
        return { plannerData: null, plannerDetails: [] };
      }

      if (entry.plannerDayWise) {
        return {
          plannerData: entry.plannerDayWise,
          plannerDetails: Array.isArray(entry.plannerDayWiseDetails)
            ? entry.plannerDayWiseDetails
            : [],
        };
      }

      if (entry.id && (entry.subtopicID != null || entry.dtFrom)) {
        return {
          plannerData: entry,
          plannerDetails: Array.isArray(entry.plannerDayWiseDetails)
            ? entry.plannerDayWiseDetails
            : [],
        };
      }

      return { plannerData: null, plannerDetails: [] };
    },

    getPlannerSubtopicID(entry) {
      const { plannerData } = this.extractPlannerEntry(entry);
      if (!plannerData) return null;
      const raw =
        plannerData.subtopicID ??
        plannerData.subTopicID ??
        plannerData.SubtopicID ??
        null;
      if (raw === null || raw === undefined || raw === "") return null;
      const n = Number(raw);
      return Number.isNaN(n) ? null : n;
    },

    resetPlannerSheetData() {
      this.plannerData = {
        duration: "",
        date: "",
        pedagogy: [],
        objectives: "",
      };
      this.plannerTableData = [];
    },

    async loadChaps() {
      var obj = {
        url:
          this.$store.state.domain +
          "QuizChapters?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      this.chapters = await this.get(obj);

    },

    openChap() {
      this.chapObj = {
        id: 0,
        chapter: "",
        subjectID: 0,
        campusID: this.$store.state.userData.cId,
      };

      this.visibility3 = true;
      this.buttonIcon = "PlusIcon";

      var elem = this.$refs["chapter"];
      elem.state = undefined;
      var elem = this.$refs["ch_sub"];
      elem.state = undefined;
    },

    editChap(item) {
      this.chapObj = { ...item };
      this.buttonIcon = "Edit2Icon";

      var elem = this.$refs["chapter"];
      elem.state = undefined;
      var elem = this.$refs["ch_sub"];
      elem.state = undefined;
    },

    checkChap() {
      var elem = this.$refs["chapter"];
      if (this.chapObj.chapter.trim() == "") {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckChSub() {
      var elem = this.$refs["ch_sub"];
      if (this.chapObj.subjectID == 0) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    async AddChap() {
      this.checkChap();
      this.CheckChSub();
      if (this.checkChap() == true && this.CheckChSub() == true) {
        if (this.chapObj.id == 0) {
          //Add
          this.chapLoading = true;
          var status = await this.post({
            url:
              this.$store.state.domain +
              "QuizChapters?db=" +
              this.$store.state.userData.db +
              "&cID=" +
              this.$store.state.userData.cId,
            body: this.chapObj,
            message: "Chapter added successfully.",
            context: this,
            token: this.$store.state.userData.token,
          });
          this.chapLoading = false;
          if (status) {
            this.loadChaps();
            this.chapObj = {
              id: 0,
              chapter: "",
              subjectID: 0,
              campusID: this.$store.state.userData.cId,
            };
            this.buttonIcon = "PlusIcon";
          }
        } else {
          //Edit
          this.chapLoading = true;
          var status = await this.put({
            url:
              this.$store.state.domain +
              "QuizChapters/" +
              this.chapObj.id +
              "?db=" +
              this.$store.state.userData.db,
            body: this.chapObj,
            message: "Chapter updated successfully.",
            context: this,
            token: this.$store.state.userData.token,
          });
          this.chapLoading = false;
          if (status) {
            this.loadChaps();
            this.chapObj = {
              id: 0,
              chapter: "",
              subjectID: 0,
              campusID: this.$store.state.userData.cId,
            };
            this.buttonIcon = "PlusIcon";
          }
        }
      }
    },

    async deleteChap(id) {
      let result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: this.$t("Yes, Delete it!"),
      });

      if (result.isConfirmed) {
        var status = await this.delete({
          url:
            this.$store.state.domain +
            "QuizChapters/" +
            id +
            "?db=" +
            this.$store.state.userData.db,
          message: "Chapter removed successfully.",
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) this.loadChaps();
      }
    },

    async loadTypes() {
      var obj = {
        url:
          this.$store.state.domain +
          "QuizQuestionType?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      this.quesTypes = await this.get(obj);

    },

    openType() {
      this.typeObj = {
        id: 0,
        questionType: "",
        campusID: this.$store.state.userData.cId,
      };

      this.visibility4 = true;
      this.buttonIcon = "PlusIcon";

      var elem = this.$refs["q_type"];
      elem.state = undefined;
    },

    editType(item) {
      this.typeObj = { ...item };
      this.buttonIcon = "Edit2Icon";

      var elem = this.$refs["q_type"];
      elem.state = undefined;
    },

    checkType() {
      var elem = this.$refs["q_type"];
      if (this.typeObj.questionType.trim() == "") {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },

    async AddType() {
      if (this.checkType() == true) {
        if (this.typeObj.id == 0) {
          //Add
          this.typeLoading = true;
          var status = await this.post({
            url:
              this.$store.state.domain +
              "QuizQuestionType?db=" +
              this.$store.state.userData.db +
              "&cID=" +
              this.$store.state.userData.cId,
            body: this.typeObj,
            message: "Quiz Type added successfully.",
            context: this,
            token: this.$store.state.userData.token,
          });
          this.typeLoading = false;
          if (status) {
            this.loadTypes();
            this.typeObj = {
              id: 0,
              questionType: "",
              campusID: this.$store.state.userData.cId,
            };
            this.buttonIcon = "PlusIcon";
          }
        } else {
          //Edit
          this.typeLoading = true;
          var status = await this.put({
            url:
              this.$store.state.domain +
              "QuizQuestionType/" +
              this.typeObj.id +
              "?db=" +
              this.$store.state.userData.db,
            body: this.typeObj,
            message: "Quiz Type updated successfully.",
            context: this,
            token: this.$store.state.userData.token,
          });
          this.typeLoading = false;
          if (status) {
            this.loadTypes();
            this.typeObj = {
              id: 0,
              questionType: "",
              campusID: this.$store.state.userData.cId,
            };
            this.buttonIcon = "PlusIcon";
          }
        }
      }
    },

    async deleteType(id) {
      let result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: this.$t("Yes, Delete it!"),
      });

      if (result.isConfirmed) {
        var status = await this.delete({
          url:
            this.$store.state.domain +
            "QuizQuestionType/" +
            id +
            "?db=" +
            this.$store.state.userData.db,
          message: "Quiz Type removed successfully.",
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) this.loadTypes();
      }
    },
    async loadTopics() {

      // Reset subtopic when subject changes
      this.topics = [];
      this.subtopics = [];
      this.allSubtopics = [];
      this.filterObj.topicID = null;
      this.filterObj.subtopicID = null;
      this.subtopicPlannerMap = {};
      this.subtopicPlannerRemap = {};
      this.topicMetaCacheBySession = {};
      this.clearSubtopicSelection();

      if (
        !this.myObj.clsID ||
        this.secIDs === null ||
        this.secIDs === undefined ||
        !this.myObj.sessionID ||
        !this.myObj.subjectID
      ) {
        this.showPlannerData = false;
        return;
      }

      // Show the planner data section immediately and set loading state
      this.showPlannerData = true;
      this.otherLoading = true;

      try {
        var obj = {
          url:
            this.$store.state.domain +
            "PlannerSheet/LoadTopicsWithSubtopics/" +
            this.plannerCampusID +
            "?classID=" +
            this.myObj.clsID +
            "&sectionID=" +
            this.resolvePlannerSectionID() +
            "&subjectID=" +
            this.myObj.subjectID +
            "&sessionID=" +
            (this.myObj.sessionID || 0) +
            "&campusID=" +
            this.plannerCampusID +
            "&db=" +
            this.$store.state.userData.db,
          token: this.$store.state.userData.token,
        };

        const response = await this.get(obj);
        const topicRows = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
          ? response.data
          : [];

        // Handle the API response structure - response is directly an array
        if (topicRows.length) {
          // Filter out items with mainTopicID = 0 or null mainTopicName
          const validTopicData = topicRows.filter(
            (item) =>
              item.mainTopicID > 0 &&
              item.mainTopicName &&
              item.mainTopicName.trim() !== ""
          );

          // Map the response to topics array
          this.topics = validTopicData.map((item) => ({
            id: item.mainTopicID,
            name: item.mainTopicName,
          }));

          // Remove duplicates from topics
          const uniqueTopics = this.topics.filter(
            (topic, index, self) =>
              self.findIndex((t) => t.id === topic.id) === index
          );
          this.topics = uniqueTopics;

          // Store ALL subtopics from valid topics
          this.allSubtopics = validTopicData.reduce((acc, topic) => {
            if (
              topic.subTopics &&
              Array.isArray(topic.subTopics) &&
              topic.subTopics.length > 0
            ) {
              const mappedSubtopics = topic.subTopics
                .map((sub) => ({
                  id: Number(sub.subTopicID ?? sub.subtopicID ?? sub.id),
                  name: sub.subTopicName || sub.subtopicName || sub.name,
                  conceptID: sub.conceptID,
                  mainTopicID: Number(topic.mainTopicID),
                  mainTopicName: topic.mainTopicName,
                }))
                .filter((sub) => sub.id && !Number.isNaN(sub.id));
              return [...acc, ...mappedSubtopics];
            }
            return acc;
          }, []);

          // Show all subtopics initially
          this.subtopics = [...this.allSubtopics];

          // Force Vue to update the component
          this.$forceUpdate();

          // Small delay to ensure reactive updates
          this.$nextTick(() => {
          });
        } else {
          // Initialize empty arrays if no valid data
          this.topics = [];
          this.subtopics = [];
          this.allSubtopics = [];
        }
      } catch (error) {
        console.error("Error loading topics and subtopics:", error);
        // Initialize empty arrays on error
        this.topics = [];
        this.subtopics = [];
        this.allSubtopics = [];

        this.$bvToast.toast("Failed to load topics and subtopics", {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
      }

      await this.loadSavedPlanners();

      // Automatically trigger the planner sheet API
      this.loadPlannerSheet();

      // Clear loading after planners are mapped so status badges are correct
      this.otherLoading = false;
    },
    async loadSubtopics() {
      // Reset subtopic selection when topic changes
      this.filterObj.subtopicID = null;

      // Filter subtopics based on selected topic
      if (!this.filterObj.topicID) {
        // If no topic selected, show all subtopics in the table but none in the filter
        this.subtopics = [...this.allSubtopics];
        return;
      }

      // Filter subtopics to show only those belonging to the selected topic
      // For the table display
      this.subtopics = this.allSubtopics.filter(
        (subtopic) => subtopic.mainTopicID === this.filterObj.topicID
      );

      // Call planner sheet API when topic changes
      this.loadPlannerSheet();
    },
    async loadPlannerSheet() {
      try {
        const { success, entries } = await this.fetchPlannerDayWise(
          this.filterObj.topicID || 0,
          this.filterObj.subtopicID || 0
        );

        this.showPlannerData = true;

        if (!success || !entries.length) {
          this.resetPlannerSheetData();
          return;
        }

        const entry = entries[0];
        const plannerResponse = entry.plannerDayWise || entry;

        if (!plannerResponse || typeof plannerResponse !== "object") {
          this.resetPlannerSheetData();
          return;
        }

        this.plannerData = {
          duration: plannerResponse.duration || "",
          date: plannerResponse.date || "",
          pedagogy: plannerResponse.pedagogy || [],
          objectives: plannerResponse.objectives || "",
        };

        const details =
          entry.plannerDayWiseDetails ||
          plannerResponse.plannerDayWiseDetails ||
          plannerResponse.syllabus ||
          [];

        this.plannerTableData = Array.isArray(details) ? details : [];
      } catch (error) {
        console.error("Error loading planner data:", error);
        this.resetPlannerSheetData();
        this.showPlannerData = true;

        this.$bvToast.toast("Failed to load planner data", {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
      }
    },
    async loadSavedPlanners() {
      try {
        const { success, entries } = await this.fetchPlannerDayWise(0, 0);

        this.savedPlanners = success && entries.length ? entries : [];
        await this.buildSubtopicPlannerMap();

      } catch (error) {
        console.error("Error loading saved planners:", error);
        this.savedPlanners = [];
        this.subtopicPlannerMap = {};
        this.subtopicPlannerRemap = {};
      }
    },

    normalizePlannerName(value) {
      return String(value || "")
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim();
    },

    getPlannerMainTopicID(entry) {
      const { plannerData } = this.extractPlannerEntry(entry);
      if (!plannerData) return null;
      const raw =
        plannerData.mainTopicID ??
        plannerData.mainTopicId ??
        plannerData.MainTopicID ??
        null;
      if (raw === null || raw === undefined || raw === "") return null;
      const n = Number(raw);
      return Number.isNaN(n) ? null : n;
    },

    async fetchTopicsWithSubtopicsForSession(sessionID) {
      const sid = Number(sessionID) || 0;
      if (this.topicMetaCacheBySession[sid]) {
        return this.topicMetaCacheBySession[sid];
      }

      const url =
        this.$store.state.domain +
        "PlannerSheet/LoadTopicsWithSubtopics/" +
        this.plannerCampusID +
        "?classID=" +
        (this.myObj.clsID || 0) +
        "&sectionID=" +
        this.resolvePlannerSectionID() +
        "&subjectID=" +
        (this.myObj.subjectID || 0) +
        "&sessionID=" +
        sid +
        "&campusID=" +
        this.plannerCampusID +
        "&db=" +
        this.$store.state.userData.db;

      try {
        const response = await axios.get(url, {
          headers: {
            Authorization: `bearer ${this.$store.state.userData.token}`,
          },
        });
        const raw = response.data;
        const list = Array.isArray(raw)
          ? raw
          : Array.isArray(raw && raw.data)
          ? raw.data
          : [];
        this.$set(this.topicMetaCacheBySession, sid, list);
        return list;
      } catch (error) {
        console.error("Failed loading topics for session", sid, error);
        this.$set(this.topicMetaCacheBySession, sid, []);
        return [];
      }
    },

    buildSubtopicMetaIndex(topicRows = []) {
      const byId = {};
      (topicRows || []).forEach((topic) => {
        const mainTopicID = Number(topic.mainTopicID);
        const mainTopicName = topic.mainTopicName || "";
        const subs = topic.subTopics || topic.subtopics || [];
        (subs || []).forEach((sub) => {
          const id = Number(sub.subTopicID ?? sub.subtopicID ?? sub.id);
          if (!id || Number.isNaN(id)) return;
          byId[id] = {
            id,
            name: sub.subTopicName || sub.subtopicName || sub.name || "",
            mainTopicID,
            mainTopicName,
          };
        });
      });
      return byId;
    },

    async resolveOrphanSubtopicMeta(orphanIds = []) {
      const needed = new Set(
        (orphanIds || []).map((id) => Number(id)).filter((id) => id && !Number.isNaN(id))
      );
      const result = {};
      if (!needed.size) return result;

      const sessionOrder = [];
      const currentSession = Number(this.myObj.sessionID) || 0;
      (this.sessions || []).forEach((session) => {
        const id = Number(session.id);
        if (!id || id === currentSession) return;
        sessionOrder.push(id);
      });

      for (const sessionID of sessionOrder) {
        if (!needed.size) break;
        const rows = await this.fetchTopicsWithSubtopicsForSession(sessionID);
        const index = this.buildSubtopicMetaIndex(rows);
        needed.forEach((id) => {
          if (index[id]) {
            result[id] = index[id];
            needed.delete(id);
          }
        });
      }

      return result;
    },

    getPlannerMetaFromEntry(entry) {
      const { plannerData } = this.extractPlannerEntry(entry);
      return {
        mainTopic:
          entry?.meta_mainTopic ||
          entry?.topic ||
          plannerData?.meta_mainTopic ||
          "",
        subTopic:
          entry?.meta_subTopic ||
          entry?.subTopic ||
          plannerData?.meta_subTopic ||
          "",
      };
    },

    findCurrentSubtopicByMeta(metaMain, metaSub, mainTopicID = null) {
      const targetSub = this.normalizePlannerName(metaSub);
      const targetMain = this.normalizePlannerName(metaMain);
      if (!targetSub) return null;

      const list = this.allSubtopics || [];

      if (targetMain) {
        const exactMatch = list.find(
          (sub) =>
            this.normalizePlannerName(sub.name) === targetSub &&
            this.normalizePlannerName(sub.mainTopicName) === targetMain
        );
        if (exactMatch) return exactMatch;
      }

      if (mainTopicID) {
        const topicMatch = list.find(
          (sub) =>
            this.normalizePlannerName(sub.name) === targetSub &&
            Number(sub.mainTopicID) === Number(mainTopicID)
        );
        if (topicMatch) return topicMatch;
      }

      const nameMatches = list.filter(
        (sub) => this.normalizePlannerName(sub.name) === targetSub
      );
      if (nameMatches.length === 1) return nameMatches[0];

      return null;
    },

    findCurrentSubtopicForOrphan(meta, sourceMainTopicID) {
      if (!meta || !meta.name) return null;
      return this.findCurrentSubtopicByMeta(
        meta.mainTopicName,
        meta.name,
        sourceMainTopicID
      );
    },

    registerPlannerOnSubtopic(map, remap, targetId, mainTopicID, sourceSubtopicID) {
      const currentId = Number(targetId);
      if (!currentId || Number.isNaN(currentId)) return;

      map[currentId] = (map[currentId] || 0) + 1;
      if (!remap[currentId]) {
        remap[currentId] = {
          mainTopicID: mainTopicID || 0,
          subtopicID: sourceSubtopicID || currentId,
        };
      }
    },

    async buildSubtopicPlannerMap() {
      const map = {};
      const remap = {};
      const currentIds = new Set(
        (this.allSubtopics || [])
          .map((sub) => Number(sub.id))
          .filter((id) => id && !Number.isNaN(id))
      );

      const orphans = [];

      (this.savedPlanners || []).forEach((entry) => {
        const subtopicID = this.getPlannerSubtopicID(entry);
        const mainTopicID = this.getPlannerMainTopicID(entry);
        const meta = this.getPlannerMetaFromEntry(entry);

        if (
          subtopicID != null &&
          subtopicID !== 0 &&
          currentIds.has(subtopicID)
        ) {
          this.registerPlannerOnSubtopic(
            map,
            remap,
            subtopicID,
            mainTopicID,
            subtopicID
          );
          return;
        }

        const metaMatch = this.findCurrentSubtopicByMeta(
          meta.mainTopic,
          meta.subTopic,
          mainTopicID
        );
        if (metaMatch) {
          this.registerPlannerOnSubtopic(
            map,
            remap,
            metaMatch.id,
            mainTopicID || metaMatch.mainTopicID,
            subtopicID || metaMatch.id
          );
          return;
        }

        if (subtopicID == null || subtopicID === 0) return;

        orphans.push({ entry, subtopicID, mainTopicID, meta });
      });

      if (orphans.length && (this.allSubtopics || []).length) {
        const orphanMeta = await this.resolveOrphanSubtopicMeta(
          orphans.map((item) => item.subtopicID)
        );

        orphans.forEach((orphan) => {
          let match = this.findCurrentSubtopicByMeta(
            orphan.meta?.mainTopic,
            orphan.meta?.subTopic,
            orphan.mainTopicID
          );

          if (!match) {
            const resolvedMeta = orphanMeta[orphan.subtopicID];
            match = this.findCurrentSubtopicForOrphan(
              resolvedMeta,
              orphan.mainTopicID
            );
          }

          if (!match) return;

          this.registerPlannerOnSubtopic(
            map,
            remap,
            match.id,
            orphan.mainTopicID || match.mainTopicID,
            orphan.subtopicID
          );
        });
      }

      this.subtopicPlannerMap = { ...map };
      this.subtopicPlannerRemap = { ...remap };
    },
    hasSavedDailyPlanner(subtopic) {
      if (!subtopic || subtopic.id == null || subtopic.id === "") return false;
      const id = Number(subtopic.id);
      if (Number.isNaN(id)) return false;
      return (this.subtopicPlannerMap[id] || 0) > 0;
    },
    getSubtopicPlanCount(subtopic) {
      if (!subtopic || subtopic.id == null || subtopic.id === "") return 0;
      const id = Number(subtopic.id);
      if (Number.isNaN(id)) return 0;
      return this.subtopicPlannerMap[id] || 0;
    },
    canShowSubtopicActions(subtopic) {
      const canView =
        this.rights &&
        this.rights.view &&
        this.hasSavedDailyPlanner(subtopic);
      const canAdd = this.rights && this.rights.add;
      return canView || canAdd;
    },
    getSubtopicSkeletonWidth(columnIndex) {
      const widths = this.canReplicate
        ? ["30%", "30%", "70%", "85%", "55%", "65%"]
        : ["35%", "75%", "85%", "55%", "65%"];
      return widths[columnIndex] || "60%";
    },

    getSubtopicSelectionKey(item) {
      return `${item.mainTopicID}_${item.id}`;
    },

    isSubtopicSelected(item) {
      return this.selectedSubtopicKeys.includes(
        this.getSubtopicSelectionKey(item)
      );
    },

    getSelectedSubtopics() {
      return (this.subtopics || []).filter((item) =>
        this.isSubtopicSelected(item)
      );
    },

    buildReplicateTopicArrays(selectedSubtopics = []) {
      const mainTopicIDs = [];
      const subtopicIDs = [];

      selectedSubtopics.forEach((item) => {
        if (!item || item.id == null || item.mainTopicID == null) return;
        // Pair by index: repeat mainTopicID once for each selected subtopic
        mainTopicIDs.push(item.mainTopicID);
        subtopicIDs.push(item.id);
      });

      return { mainTopicIDs, subtopicIDs };
    },

    validateReplicatePayload(body = {}) {
      if (!body.sourceSessionID || !body.sessionID) {
        return "Source and destination session are required";
      }
      if (body.sourceSessionID === body.sessionID) {
        return "Source and destination session cannot be the same";
      }
      if (!body.mainTopicIDs?.length || !body.subtopicIDs?.length) {
        return "Select at least one topic/subtopic";
      }
      if (body.mainTopicIDs.length !== body.subtopicIDs.length) {
        return "mainTopicIDs and subtopicIDs must have the same count";
      }
      return null;
    },

    toggleSubtopicSelection(item) {
      if (!this.hasSavedDailyPlanner(item)) return;

      const key = this.getSubtopicSelectionKey(item);
      const index = this.selectedSubtopicKeys.indexOf(key);
      if (index === -1) {
        this.selectedSubtopicKeys.push(key);
      } else {
        this.selectedSubtopicKeys.splice(index, 1);
      }
    },

    selectAllSubtopics(checked) {
      if (checked) {
        this.replicableSubtopics.forEach((item) => {
          const key = this.getSubtopicSelectionKey(item);
          if (!this.selectedSubtopicKeys.includes(key)) {
            this.selectedSubtopicKeys.push(key);
          }
        });
      } else {
        const currentKeys = this.subtopics.map((item) =>
          this.getSubtopicSelectionKey(item)
        );
        this.selectedSubtopicKeys = this.selectedSubtopicKeys.filter(
          (key) => !currentKeys.includes(key)
        );
      }
    },

    clearSubtopicSelection() {
      this.selectedSubtopicKeys = [];
    },

    getDefaultReplicateSessionID() {
      const options = this.replicateSessionOptions;
      if (!options.length) return null;
      const currentSession = options.find((el) => el.isCurrentSession === true);
      return currentSession
        ? currentSession.id
        : options[options.length - 1].id;
    },

    getSessionNameById(sessionID) {
      const session = this.sessions.find((s) => s.id === sessionID);
      return session ? session.session : "—";
    },

    async openReplicateModal() {
      if (!this.canOpenReplicate) {
        this.$bvToast.toast(
          "Select class, section, subject, session and at least one planned subtopic.",
          {
            title: this.$t("Error"),
            variant: "warning",
            solid: true,
          }
        );
        return;
      }

      this.replicateSummary = null;
      this.replicateResults = [];
      this.replicateSessionID = this.getDefaultReplicateSessionID();
      this.showReplicateModal = true;

      if (!this.sessions.length) {
        this.replicateSessionsLoading = true;
        try {
          await this.loadSession();
          this.replicateSessionID = this.getDefaultReplicateSessionID();
        } finally {
          this.replicateSessionsLoading = false;
        }
      }
    },

    resetReplicateModal() {
      if (this.replicating) return;
      this.replicateSessionID = null;
      this.replicateSummary = null;
      this.replicateResults = [];
    },

    finishReplicate() {
      this.showReplicateModal = false;
      this.clearSubtopicSelection();
      this.loadSavedPlanners();
    },

    replicateStatusVariant(status) {
      const value = String(status || "").toLowerCase();
      if (value === "success") return "success";
      if (value === "skipped") return "warning";
      if (value === "failed" || value === "error") return "danger";
      return "secondary";
    },

    async replicateDayWisePlanners() {
      if (!this.replicateSessionID || !this.selectedSubtopicKeys.length) {
        this.$bvToast.toast("Please select a destination session.", {
          title: this.$t("Error"),
          variant: "warning",
          solid: true,
        });
        return;
      }

      if (this.replicateSessionID === this.myObj.sessionID) {
        this.$bvToast.toast(
          "Destination session must be different from the source session.",
          {
            title: this.$t("Error"),
            variant: "warning",
            solid: true,
          }
        );
        return;
      }

      const selectedSubtopics = this.getSelectedSubtopics();
      if (!selectedSubtopics.length) {
        this.$bvToast.toast("No valid subtopics selected for replication.", {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      // API pairs by index: mainTopicIDs[i] ↔ subtopicIDs[i]
      // Repeat each main topic ID once per selected subtopic under it.
      const { mainTopicIDs, subtopicIDs } =
        this.buildReplicateTopicArrays(selectedSubtopics);

      if (!mainTopicIDs.length || !subtopicIDs.length) {
        this.$bvToast.toast(
          "Select at least one main topic with a subtopic.",
          {
            title: this.$t("Error"),
            variant: "warning",
            solid: true,
          }
        );
        return;
      }

      if (mainTopicIDs.length !== subtopicIDs.length) {
        this.$bvToast.toast(
          "mainTopicIDs and subtopicIDs must have the same count.",
          {
            title: this.$t("Error"),
            variant: "danger",
            solid: true,
          }
        );
        return;
      }

      const body = {
        classID: this.myObj.clsID,
        sectionID: this.resolvePlannerSectionID(),
        subjectID: this.myObj.subjectID,
        sourceSessionID: this.myObj.sessionID,
        sessionID: this.replicateSessionID,
        mainTopicIDs,
        subtopicIDs,
      };

      const validationError = this.validateReplicatePayload(body);
      if (validationError) {
        this.$bvToast.toast(validationError, {
          title: this.$t("Error"),
          variant: "warning",
          solid: true,
        });
        return;
      }

      this.replicating = true;
      this.replicateSummary = null;
      this.replicateResults = [];

      try {
        const params = new URLSearchParams({
          db: this.$store.state.userData.db,
          userID: String(
            this.$store.state.userData.userID || this.$store.state.userData.id
          ),
        });

        const url = `${this.$store.state.domain}PlannerDayWise/Replicate/${this.$store.state.userData.cId}?${params.toString()}`;

        const response = await axios.post(url, body, {
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            "Content-Type": "application/json",
          },
        });

        const payload = response.data || {};
        const resultData = payload.data || {};

        if (payload.status === "success" || Array.isArray(resultData.results)) {
          this.replicateSummary = {
            sessionID: resultData.sessionID || this.replicateSessionID,
            sessionName:
              resultData.sessionName ||
              this.getSessionNameById(this.replicateSessionID),
            sourceSessionName:
              resultData.sourceSessionName ||
              this.getSessionNameById(this.myObj.sessionID),
            total: resultData.totalTopics ?? selectedSubtopics.length,
            replicated: resultData.replicatedPlans ?? 0,
            skipped: resultData.skippedTopics ?? 0,
            failed: resultData.failedTopics ?? 0,
          };
          this.replicateResults = Array.isArray(resultData.results)
            ? resultData.results
            : [];

          const toastVariant =
            this.replicateSummary.failed > 0
              ? "danger"
              : this.replicateSummary.skipped > 0
              ? "warning"
              : "success";

          this.$bvToast.toast(this.replicateSummaryTitle, {
            title: "Replication Complete",
            variant: toastVariant,
            solid: true,
            autoHideDelay: 5000,
          });
        } else {
          throw new Error(payload.message || payload.data || "Replication failed.");
        }
      } catch (error) {
        console.error("Error replicating daily planners:", error);
        const message =
          error.response?.data?.message ||
          error.response?.data?.data ||
          error.message ||
          "Failed to replicate daily planners. Please try again.";

        this.$bvToast.toast(
          typeof message === "string" ? message : "Failed to replicate daily planners.",
          {
            title: this.$t("Error"),
            variant: "danger",
            solid: true,
          }
        );
      } finally {
        this.replicating = false;
      }
    },
    getDateConfig(readOnly) {
      return {
        ...this.dateConfig,
        clickOpens: !readOnly,
        allowInput: !readOnly,
      };
    },
    async addNewPlannerRow() {
      // Add a new row to the daily planner table
      this.plannerTableData.push({
        id: 0, // New row, no ID yet
        plannerDayWiseID: 0, // Will be set when saved
        lessonOrganisation: "",
        activity: "",
        resource: "",
        assessmentStrategy: "",
      });

    },
    async editPlannerRow(item, index) {
      // For inline editing, we don't need to do anything special
      // since v-model handles the two-way binding
    },
    async deletePlannerRow(item, index) {
      if (item.id == 0) {
        // Remove from local array if it's a new unsaved item
        this.plannerTableData.splice(index, 1);
      } else {
        // Delete from backend if it's a saved item
        var status = await this.delete({
          url:
            this.$store.state.domain +
            "PlannerDayWise/DeleteDetail/" +
            item.id +
            "?db=" +
            this.$store.state.userData.db,
          grading: true,
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) {
          this.plannerTableData.splice(index, 1);
        }
      }
    },
    async savePlanner() {
      // Keep the old savePlanner method for backward compatibility
      // This method is for the old planner functionality
      if (!this.canFilter) {
        this.$bvToast.toast("Please select Class and Section before saving!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      if (!this.plannerTableData.length) {
        this.$bvToast.toast("Please add at least one lesson plan entry!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      const invalidEntries = this.plannerTableData.filter(
        (entry) =>
          !entry.lessonOrganisation.trim() ||
          !entry.activity.trim() ||
          !entry.resource.trim() ||
          !entry.assessment.trim()
      );

      if (invalidEntries.length > 0) {
        this.$bvToast.toast(
          "Please fill in all fields for all lesson plan entries!",
          {
            title: this.$t("Error!"),
            variant: "danger",
            solid: true,
          }
        );
        return;
      }

      let saveObj = {
        plannerGrid: this.myObj,
        plannerData: this.plannerTableData,
        secIDs: [this.secIDs],
        filterObj: this.filterObj,
      };

      this.request = true;
      var status = await this.post({
        url:
          this.$store.state.domain +
          "PlannerData/Save?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        body: saveObj,
        message: "Planner saved successfully.",
        context: this,
        token: this.$store.state.userData.token,
      });
      this.request = false;

      if (status) {
        this.resetPlanner();
      }
    },
    async resetPlanner() {
      this.showPlannerData = false;
      this.plannerData = {};
      this.plannerTableData = [];
      this.filterObj = {
        topicID: null,
        subtopicID: null,
      };
      this.myObj = {
        clsID: 0,
        secID: 0,
        subjectID: 0,
        tID: 0,
        campusID: this.$store.state.userData.cId,
      };
      this.secIDs = null;
      this.topics = [];
      this.subtopics = [];
      this.showDailyPlanTable = false;
      this.dailyPlannerViewMode = false;
      this.selectedSubtopicForPlan = "";
      // Reset the new daily planner forms
      this.dailyPlannerForms = [];
    },
    openDailyPlanner(subtopic, viewOnly = false) {
      if (viewOnly && !this.hasSavedDailyPlanner(subtopic)) {
        this.$bvToast.toast("No saved lesson plans found for this subtopic.", {
          title: "View Only",
          variant: "info",
          solid: true,
        });
        return;
      }

      if (!viewOnly && !(this.rights && this.rights.add)) {
        return;
      }

      if (viewOnly && !(this.rights && this.rights.view)) {
        return;
      }

      this.dailyPlannerViewMode = viewOnly;
      this.selectedSubtopic = subtopic;
      this.filterObj.topicID = subtopic.mainTopicID;
      this.filterObj.subtopicID = subtopic.id;
      this.selectedSubtopicForPlan = subtopic.name;
      this.dailyPlannerForms = [];
      this.showDailyPlanTable = true;
      this.loadDailyPlannerData();
    },
    addDailyPlanner(subtopic) {
      this.openDailyPlanner(subtopic, false);
    },
    viewDailyPlanner(subtopic) {
      this.openDailyPlanner(subtopic, true);
    },
    async loadDailyPlannerData() {
      this.otherLoading = true;
      this.dailyPlannerForms = [];

      try {
        const currentTopicID = Number(this.filterObj.topicID || 0);
        const currentSubtopicID = Number(this.filterObj.subtopicID || 0);
        const remap =
          currentSubtopicID && this.subtopicPlannerRemap
            ? this.subtopicPlannerRemap[currentSubtopicID]
            : null;

        const fetchKeys = [];
        if (currentTopicID || currentSubtopicID) {
          fetchKeys.push({
            mainTopicID: currentTopicID || 0,
            subtopicID: currentSubtopicID || 0,
          });
        }
        if (
          remap &&
          (Number(remap.mainTopicID) !== currentTopicID ||
            Number(remap.subtopicID) !== currentSubtopicID)
        ) {
          fetchKeys.push({
            mainTopicID: Number(remap.mainTopicID) || 0,
            subtopicID: Number(remap.subtopicID) || 0,
          });
        }

        let success = false;
        let entries = [];

        for (const key of fetchKeys) {
          const result = await this.fetchPlannerDayWise(
            key.mainTopicID,
            key.subtopicID
          );
          if (result.success && result.entries.length) {
            success = true;
            entries = result.entries;
            break;
          }
        }

        // Fallback: filter bulk LoadSelected / cached saved planners
        if ((!success || !entries.length) && currentSubtopicID) {
          let pooled = Array.isArray(this.savedPlanners)
            ? this.savedPlanners
            : [];

          if (!pooled.length) {
            const bulk = await this.fetchPlannerDayWise(0, 0);
            if (bulk.success && bulk.entries.length) {
              pooled = bulk.entries;
              this.savedPlanners = pooled;
              await this.buildSubtopicPlannerMap();
            }
          }

          const sourceSubtopicID = Number(
            (remap && remap.subtopicID) || currentSubtopicID
          );
          const sourceTopicID = Number(
            (remap && remap.mainTopicID) || currentTopicID || 0
          );

          const matched = pooled.filter((entry) => {
            const { plannerData } = this.extractPlannerEntry(entry);
            if (!plannerData) return false;
            const sid = Number(
              plannerData.subtopicID ?? plannerData.subTopicID ?? 0
            );
            const mid = Number(
              plannerData.mainTopicID ?? plannerData.mainTopicId ?? 0
            );
            if (sid !== sourceSubtopicID && sid !== currentSubtopicID) {
              return false;
            }
            if (
              sourceTopicID &&
              mid &&
              mid !== sourceTopicID &&
              mid !== currentTopicID
            ) {
              return false;
            }
            return true;
          });

          if (matched.length) {
            success = true;
            entries = matched;
          }
        }

        entries.forEach((entry, index) => {
          const { plannerData } = this.extractPlannerEntry(entry);
          if (plannerData && plannerData.isUrdu !== undefined) {
          }
        });

        const dataArray = success && entries.length ? entries : [];

        if (dataArray.length > 0) {
          // Check if any planner has isUrdu set to true and set global layout accordingly
          const hasUrduPlanner = dataArray.some((entry) => {
            const { plannerData } = this.extractPlannerEntry(entry);
            return plannerData && plannerData.isUrdu === true;
          });

          if (hasUrduPlanner) {
            this.globalColumnLayout = 3; // Set to Urdu mode (3 columns)
          } else {
            this.globalColumnLayout = 4; // Set to English mode (4 columns) - default for null/false/undefined
          }

          // Create a form for each planner entry
          dataArray.forEach((plannerEntry, index) => {

            const extracted = this.extractPlannerEntry(plannerEntry);
            const plannerData = extracted.plannerData;
            const plannerDetails = extracted.plannerDetails;

            if (!plannerData) {
              return;
            }

            // Convert planner details to table data format with better property handling
            const tableData = plannerDetails.map((detail, detailIndex) => {

              // Handle different possible property names (case-insensitive)
              const lessonOrg =
                detail.lessonOrganisation ||
                detail.LessonOrganisation ||
                detail.lessonorganisation ||
                detail.lesson_organisation ||
                "";
              const activity = detail.activity || detail.Activity || "";
              const resource = detail.resource || detail.Resource || "";
              const assessment =
                detail.assessmentStrategy ||
                detail.AssessmentStrategy ||
                detail.assessment_strategy ||
                detail.assessment ||
                "";

              return [lessonOrg, activity, resource, assessment];
            });

            if (!this.dailyPlannerViewMode) {
              tableData.push(["", "", "", ""]);
              tableData.push(["", "", "", ""]);
            }

            const formData = {
              id: plannerData.id,
              dtFrom: plannerData.dtFrom
                ? plannerData.dtFrom.split("T")[0]
                : "",
              dayFromName: plannerData.dayFromName || "",
              dtTo: plannerData.dtTo ? plannerData.dtTo.split("T")[0] : "",
              dayToName: plannerData.dayToName || "",
              duration: plannerData.duration || "",
              columnLayout: 4, // Default to 4 columns for loaded data
              tableData: tableData,
              saving: false,
            };

            this.dailyPlannerForms.push(formData);
          });

          // Force Vue reactivity update and wait for DOM to be ready
          this.$forceUpdate();

          // Use multiple $nextTick calls to ensure DOM is fully ready
          this.$nextTick(() => {
            this.$nextTick(() => {
              setTimeout(() => {
                this.dailyPlannerForms.forEach((form, index) => {
                  // Stagger the initialization with delays
                  setTimeout(() => {
                    this.initializeNewTable(index);
                  }, index * 100); // 100ms delay between each table
                });
              }, 300); // Initial delay to ensure DOM is ready
            });
          });
        } else if (!this.dailyPlannerViewMode) {
          this.addNewPlannerForm();
        }
      } catch (error) {
        console.error("Error loading planner data:", error);
        if (!this.dailyPlannerViewMode) {
          this.addNewPlannerForm();
        }
      } finally {
        this.otherLoading = false;
      }
    },
    addNewPlannerForm() {
      const newForm = {
        id: null,
        dtFrom: "",
        dayFromName: "",
        dtTo: "",
        dayToName: "",
        duration: "",
        columnLayout: this.globalColumnLayout, // Use current global layout
        tableData: [
          ["", "", "", ""],
          ["", "", "", ""],
          ["", "", "", ""],
          ["", "", "", ""],
          ["", "", "", ""],
        ],
        saving: false,
      };
      this.dailyPlannerForms.push(newForm);

      const newFormIndex = this.dailyPlannerForms.length - 1;

      // Initialize the table with improved timing
      this.$nextTick(() => {
        this.$nextTick(() => {
          setTimeout(() => {
            this.initializeNewTable(newFormIndex);
          }, 200);
        });
      });
    },
    initializeNewTable(formIndex, retryCount = 0) {
      const refName = `hotTable_${formIndex}`;
      const maxRetries = 5;

      // Wait a bit for the DOM to update
      setTimeout(() => {
        let hotTableRef = this.$refs[refName];

        if (Array.isArray(hotTableRef)) {
          hotTableRef = hotTableRef[0];
        }

        if (hotTableRef && hotTableRef.hotInstance) {
          const form = this.dailyPlannerForms[formIndex];

          if (form && form.tableData && form.tableData.length > 0) {
            try {
              hotTableRef.hotInstance.loadData(form.tableData);

              // Force a render to ensure data is displayed
              setTimeout(() => {
                if (hotTableRef.hotInstance) {
                  hotTableRef.hotInstance.render();
                }
              }, 50);
            } catch (error) {
              console.error(`❌ Error initializing table ${formIndex}:`, error);

              // Try retry if we haven't exceeded max retries
              if (retryCount < maxRetries) {
                this.initializeNewTable(formIndex, retryCount + 1);
              }
            }
          } else {
          }
        } else {

          // Retry if not found and we haven't exceeded max retries
          if (retryCount < maxRetries) {
            setTimeout(() => {
              this.initializeNewTable(formIndex, retryCount + 1);
            }, 200); // Longer delay for retries
          } else {
            console.error(
              `❌ Failed to initialize table ${formIndex} after ${maxRetries} attempts`
            );
          }
        }
      }, 100 + retryCount * 100); // Progressive delay
    },
    async removePlannerForm(index) {
      if (this.dailyPlannerForms.length > 1) {
        const form = this.dailyPlannerForms[index];

        // Check if the form has an ID (meaning it's saved in the database)
        if (form.id) {
          try {
            // Show confirmation dialog
            const confirmResult = await this.$swal.fire({
              title: this.$t("Are you sure?"),
              text: "This will permanently delete the lesson plan. You won't be able to revert this!",
              icon: "warning",
              showCancelButton: true,
              confirmButtonColor: "#d33",
              cancelButtonColor: "#3085d6",
              confirmButtonText: this.$t("Yes, Delete it!"),
              cancelButtonText: this.$t("Cancel"),
            });

            if (!confirmResult.isConfirmed) {
              return; // User cancelled
            }

            // Add to deleting forms array to show loading state AFTER user confirms
            this.deletingForms.push(index);

            // Make DELETE API call
            const apiUrl =
              this.$store.state.domain +
              `PlannerDayWise/DeletePlannerDayWise?db=${this.$store.state.userData.db}&campusID=${this.$store.state.userData.cId}&id=${form.id}`;

            const response = await fetch(apiUrl, {
              method: "DELETE",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${this.$store.state.userData.token}`,
              },
            });

            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }

            const apiResult = await response.json();

            // If API call successful, remove from UI
            this.dailyPlannerForms.splice(index, 1);

            // Force Vue to update the DOM
            this.$nextTick(() => {
              this.$forceUpdate();

              // Reinitialize tables for remaining forms to ensure proper indexing
              this.dailyPlannerForms.forEach((form, newIndex) => {
                setTimeout(() => {
                  this.initializeNewTable(newIndex);
                }, 100 * (newIndex + 1));
              });
            });

            // Show success message
            this.$bvToast.toast("Lesson plan deleted successfully!", {
              title: "Success!",
              variant: "success",
              solid: true,
            });
          } catch (error) {
            console.error("Error deleting planner form:", error);
            this.$bvToast.toast(
              "Failed to delete lesson plan. Please try again.",
              {
                title: this.$t("Error!"),
                variant: "danger",
                solid: true,
              }
            );
            // Remove from deleting forms array on error
            this.deletingForms = this.deletingForms.filter((i) => i !== index);
            return; // Don't remove from UI if API call failed
          }
        } else {
          // Form is not saved yet, just remove from UI
          // Add to deleting forms array to show loading state for unsaved forms
          this.deletingForms.push(index);

          this.dailyPlannerForms.splice(index, 1);

          // Force Vue to update the DOM
          this.$nextTick(() => {
            this.$forceUpdate();

            // Reinitialize tables for remaining forms to ensure proper indexing
            this.dailyPlannerForms.forEach((form, newIndex) => {
              setTimeout(() => {
                this.initializeNewTable(newIndex);
              }, 100 * (newIndex + 1));
            });
          });

          // Show success message
          this.$bvToast.toast("Unsaved lesson plan removed successfully!", {
            title: "Success!",
            variant: "success",
            solid: true,
          });
        }

        // Remove from deleting forms array after successful removal
        this.deletingForms = this.deletingForms.filter((i) => i !== index);
      } else {
        // Show warning if trying to remove the last form
        this.$bvToast.toast(
          "Cannot remove the last lesson plan. At least one plan is required.",
          {
            title: "Warning!",
            variant: "warning",
            solid: true,
          }
        );
      }
    },
    async savePlannerForm(formIndex, suppressToast = false) {
      const form = this.dailyPlannerForms[formIndex];

      // Validate that we have the required filter selections
      if (
        !this.myObj.clsID ||
        this.secIDs === null ||
        this.secIDs === undefined ||
        !this.myObj.sessionID ||
        !this.myObj.subjectID
      ) {
        this.$bvToast.toast(
          "Please select Class, Section, Session, and Subject before saving!",
          {
            title: this.$t("Error!"),
            variant: "danger",
            solid: true,
          }
        );
        return;
      }

      // Get current data from the specific HandsOnTable
      let currentData = [];

      // Try multiple ways to get the table reference
      const refName = `hotTable_${formIndex}`;
      let hotTableRef = this.$refs[refName];

      // If it's an array, get the first element
      if (Array.isArray(hotTableRef)) {
        hotTableRef = hotTableRef[0];
      }

      if (!hotTableRef || !hotTableRef.hotInstance) {
        // Fallback: try to get data from form.tableData if table ref is not available
        if (form.tableData && form.tableData.length > 0) {
          currentData = form.tableData;
        } else {
          this.$bvToast.toast("Table not ready. Please try again.", {
            title: this.$t("Error!"),
            variant: "danger",
            solid: true,
          });
          return;
        }
      } else {
        try {
          currentData = hotTableRef.hotInstance.getData();
        } catch (error) {
          console.error("Error getting data from HandsOnTable:", error);
          // Fallback to form data
          if (form.tableData && form.tableData.length > 0) {
            currentData = form.tableData;
          } else {
            this.$bvToast.toast("Failed to get table data. Please try again.", {
              title: this.$t("Error!"),
              variant: "danger",
              solid: true,
            });
            return;
          }
        }
      }

      // Filter out empty rows and convert to our format
      const filledRows = currentData
        .filter(
          (row) =>
            row &&
            row.some(
              (cell) => cell !== null && cell !== "" && cell !== undefined
            )
        )
        .map((row) => ({
          lessonOrganisation: (row[0] || "").toString().trim(),
          activity: (row[1] || "").toString().trim(),
          resource: (row[2] || "").toString().trim(),
          assessmentStrategy: (row[3] || "").toString().trim(),
        }))
        .filter(
          (row) =>
            row.lessonOrganisation ||
            row.activity ||
            row.resource ||
            row.assessmentStrategy
        );

      // Validate that we have data to save
      if (!filledRows.length) {
        this.$bvToast.toast("Please add at least one lesson plan entry!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      // Validate date range
      if (!form.dtFrom || !form.dtTo) {
        this.$bvToast.toast("Please select both From Date and To Date!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      // Validate that To Date is after From Date
      if (new Date(form.dtFrom) >= new Date(form.dtTo)) {
        this.$bvToast.toast("To Date must be after From Date!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      // Prepare the payload
      let saveObj = {
        plannerDayWise: {
          id: form.id || 0,
          classID: this.myObj.clsID,
          sectionID: this.secIDs,
          subjectID: this.myObj.subjectID,
          sessionID: this.myObj.sessionID,
          mainTopicID: this.filterObj.topicID || 0,
          subtopicID: this.filterObj.subtopicID || 0,
          dtFrom: form.dtFrom + "T00:00:00",
          dayFromName: form.dayFromName || "",
          dtTo: form.dtTo + "T00:00:00",
          dayToName: form.dayToName || "",
          duration: form.duration || "",
          campusID: this.$store.state.userData.cId,
          isActive: true,
          isUrdu: this.globalColumnLayout === 3, // true for Urdu mode (3 columns), false for English mode (4 columns)
        },
        plannerDayWiseDetails: filledRows.map((row) => ({
          id: 0,
          plannerDayWiseID: form.id || 0,
          LessonOrganisation: this.processContentForSaving(
            row.lessonOrganisation
          ),
          Activity: this.processContentForSaving(row.activity),
          Resource: this.processContentForSaving(row.resource),
          AssessmentStrategy: this.processContentForSaving(
            row.assessmentStrategy
          ),
        })),
      };

      form.saving = true;

      try {
        var status = await this.post({
          url:
            this.$store.state.domain +
            "PlannerDayWise/Save?db=" +
            this.$store.state.userData.db +
            "&campusID=" +
            this.$store.state.userData.cId +
            "&userID=" +
            (this.$store.state.userData.userID ||
              this.$store.state.userData.id ||
              1),
          body: saveObj,
          message: "Daily planner saved successfully.",
          context: this,
          token: this.$store.state.userData.token,
        });

        if (status) {
          // Update the form with the saved ID if it was a new form
          if (!form.id && status.id) {
            form.id = status.id;
          }

          // Only show individual success toast if not suppressed
          if (!suppressToast) {
            this.$bvToast.toast(
              `Lesson Plan ${formIndex + 1} saved successfully!`,
              {
                title: "Success!",
                variant: "success",
                solid: true,
              }
            );
          }
        }
      } catch (error) {
        console.error("Error saving daily planner:", error);
        this.$bvToast.toast("Failed to save lesson plan. Please try again.", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      } finally {
        form.saving = false;
      }
    },
    formatDate(dateStr) {
      const date = new Date(dateStr);
      return date.toLocaleDateString();
    },
    getTopicName(topicID) {
      const topic = this.topics.find((t) => t.id === topicID);
      return topic ? topic.name : "Topic not found";
    },
    getSubtopicName(subtopicID) {
      const subtopic = this.subtopics.find((s) => s.id === subtopicID);
      return subtopic ? subtopic.name : "Subtopic not found";
    },
    editSavedPlanner(planner) {

      // Set the filter selections to match the saved planner
      this.filterObj.topicID = planner.mainTopicID;
      this.filterObj.subtopicID = planner.subtopicID;

      // Find and set the selected subtopic for display
      const selectedSubtopic = this.allSubtopics.find(
        (s) => s.id === planner.subtopicID
      );
      if (selectedSubtopic) {
        this.selectedSubtopicForPlan = selectedSubtopic.name;
      }

      // Load the planner data into the form
      this.dailyPlannerData = {
        dtFrom: planner.dtFrom ? planner.dtFrom.split("T")[0] : "",
        dayFromName: planner.dayFromName || "",
        dtTo: planner.dtTo ? planner.dtTo.split("T")[0] : "",
        dayToName: planner.dayToName || "",
        duration: planner.duration || "",
      };

      // Load the planner details
      if (
        planner.plannerDayWiseDetails &&
        Array.isArray(planner.plannerDayWiseDetails)
      ) {
        this.plannerTableData = planner.plannerDayWiseDetails.map((detail) => ({
          id: detail.id,
          plannerDayWiseID: detail.plannerDayWiseID,
          lessonOrganisation: this.processContentForEditing(
            detail.LessonOrganisation || detail.lessonOrganisation || ""
          ),
          activity: this.processContentForEditing(
            detail.Activity || detail.activity || ""
          ),
          resource: this.processContentForEditing(
            detail.Resource || detail.resource || ""
          ),
          assessmentStrategy: this.processContentForEditing(
            detail.AssessmentStrategy || detail.assessmentStrategy || ""
          ),
        }));

        // Prepare data for HandsOnTable
        const tableData = planner.plannerDayWiseDetails.map((detail) => [
          this.processContentForEditing(
            detail.LessonOrganisation || detail.lessonOrganisation || ""
          ),
          this.processContentForEditing(
            detail.Activity || detail.activity || ""
          ),
          this.processContentForEditing(
            detail.Resource || detail.resource || ""
          ),
          this.processContentForEditing(
            detail.AssessmentStrategy || detail.assessmentStrategy || ""
          ),
        ]);

        // Add an empty row at the end for new entries
        tableData.push(["", "", "", ""]);

        // Load data into HandsOnTable with a delay to ensure it's mounted
        this.$nextTick(() => {
          setTimeout(() => {
            if (this.$refs.hotTable && this.$refs.hotTable.hotInstance) {
              this.$refs.hotTable.hotInstance.loadData(tableData);
            } else {
              console.error("HandsOnTable instance not found during edit");
            }
          }, 200); // Slightly longer delay for edit mode
        });
      } else {
        this.plannerTableData = [];

        // Initialize with empty data if no details
        const emptyData = [["", "", "", ""]];
        this.$nextTick(() => {
          setTimeout(() => {
            if (this.$refs.hotTable && this.$refs.hotTable.hotInstance) {
              this.$refs.hotTable.hotInstance.loadData(emptyData);
            }
          }, 200);
        });
      }

      // Show the daily planning table for editing
      this.showDailyPlanTable = true;

      // Store the current planner ID for updating
      this.currentEditingPlannerId = planner.id;

      this.$bvToast.toast(
        `Editing planner: ${
          selectedSubtopic ? selectedSubtopic.name : "Unknown subtopic"
        }`,
        {
          title: "Edit Mode",
          variant: "info",
          solid: true,
        }
      );
    },
    viewPlannerDetails(planner) {

      // Create a modal or alert to show planner details
      const selectedSubtopic = this.allSubtopics.find(
        (s) => s.id === planner.subtopicID
      );
      const selectedTopic = this.topics.find(
        (t) => t.id === planner.mainTopicID
      );

      let detailsHtml = `
        <div class="planner-details">
          <h6><strong>Topic:</strong> ${
            selectedTopic ? selectedTopic.name : "Unknown"
          }</h6>
          <h6><strong>Subtopic:</strong> ${
            selectedSubtopic ? selectedSubtopic.name : "Unknown"
          }</h6>
          <h6><strong>Date Range:</strong> ${this.formatDate(
            planner.dtFrom
          )} to ${this.formatDate(planner.dtTo)}</h6>
          <h6><strong>Duration:</strong> ${planner.duration}</h6>
          <hr>
          <h6><strong>Lesson Activities:</strong></h6>
        `;

      if (
        planner.plannerDayWiseDetails &&
        planner.plannerDayWiseDetails.length > 0
      ) {
        planner.plannerDayWiseDetails.forEach((detail, index) => {
          detailsHtml += `
            <div class="mb-3 p-2 border rounded">
              <strong>Activity ${index + 1}:</strong><br>
              <strong>Lesson Organisation:</strong> ${
                detail.LessonOrganisation || detail.lessonOrganisation
              }<br>
              <strong>Activity:</strong> ${
                detail.Activity || detail.activity
              }<br>
              <strong>Resource:</strong> ${
                detail.Resource || detail.resource
              }<br>
              <strong>Assessment Strategy:</strong> ${
                detail.AssessmentStrategy || detail.assessmentStrategy
              }
            </div>
          `;
        });
      } else {
        detailsHtml += '<p class="text-muted">No lesson activities found.</p>';
      }

      detailsHtml += "</div>";

      this.$bvModal.msgBoxOk(detailsHtml, {
        title: "Planner Details",
        size: "lg",
        buttonSize: "sm",
        okVariant: "primary",
        headerClass: "p-2 border-bottom-0",
        footerClass: "p-2 border-top-0",
        centered: true,
      });
    },
    onDataChange(changes, source) {
      if (source === "loadData") return;

      // Get the current data from HandsOnTable
      const tableData = this.$refs.hotTable.hotInstance.getData();

      // Convert the raw data to our plannerTableData format
      this.plannerTableData = tableData
        .filter((row) => row.some((cell) => cell !== null && cell !== ""))
        .map((row) => ({
          id: 0,
          plannerDayWiseID: this.currentEditingPlannerId || 0,
          lessonOrganisation: row[0] || "",
          activity: row[1] || "",
          resource: row[2] || "",
          assessmentStrategy: row[3] || "",
        }));

      // Trigger cleanup after data changes (with a delay to avoid conflicts)
      if (source !== "paste") {
        setTimeout(() => {
          this.cleanupEmptyRows();
        }, 200);
      }
    },

    openPrintView() {
      if (!this.canPrint) {
        this.$bvToast.toast(
          "Please select Class, Section, Session, and Subject before viewing the planner!",
          {
            title: "Missing Filters",
            variant: "warning",
            solid: true,
          }
        );
        return;
      }

      const sharedQuery = {
        db: this.$store.state.userData.db,
        cls: String(this.myObj.clsID || 0),
        sec: String(this.secIDs ?? 0),
        subjectID: String(this.myObj.subjectID || 0),
        sessionID: String(this.myObj.sessionID || 0),
        topicID: String(this.filterObj.topicID || 0),
        subTopicID: String(this.filterObj.subtopicID || 0),
        className: this.selectedGrade || "",
        subjectName: this.selectedSubject || "",
        topicName: this.selectedTopic || "",
        subtopicName: this.selectedSubtopic || "",
      };

      let route;

      if (this.reportDomain === "myskooldeenway") {
        route = this.$router.resolve({
          name: "planner-daywise-deenway",
          query: {
            ...sharedQuery,
            cID: String(this.$store.state.userData.cId || 1),
            campusName:
              this.$store.state.userData?.campusName ||
              this.$store.state.userData?.organizationName ||
              "The Deenway School",
          },
        });
      } else {
        route = this.$router.resolve({
          name: "planner-daywise-print",
          query: {
            ...sharedQuery,
            cID: String(
              this.plannerCampusID || this.$store.state.userData.cId || 1
            ),
            sectionName: this.selectedSection || "",
            sessionName: this.selectedSession || "",
            campusName:
              this.$store.state.userData?.campusName ||
              this.$store.state.userData?.organizationName ||
              this.$store.state.currentBranch?.organizationName ||
              "",
          },
        });
      }

      window.open(route.href, "_blank", "noopener,noreferrer");

      this.$bvToast.toast("Planner opened in a new tab", {
        title: "Planner View",
        variant: "success",
        solid: true,
      });
    },

    // Helper method to handle successful paste operations
    handleSuccessfulPaste(rowCount, colCount) {
      // this.$bvToast.toast(`Successfully pasted ${rowCount} rows and ${colCount} columns of data`, {
      //   title: "Paste Successful",
      //   variant: "success",
      //   solid: true,
      // });
    },

    // Helper method to clear all table data
    clearTableData() {
      if (this.$refs.hotTable && this.$refs.hotTable.hotInstance) {
        // Clear all data and reset to empty state
        this.$refs.hotTable.hotInstance.loadData([["", "", "", ""]]);
        this.plannerTableData = [];

        this.$bvToast.toast("Table data cleared successfully", {
          title: "Table Cleared",
          variant: "info",
          solid: true,
        });
      }
    },

    // Method to clean up empty rows and keep only necessary rows
    cleanupEmptyRows() {
      if (!this.$refs.hotTable || !this.$refs.hotTable.hotInstance) return;

      // Don't cleanup if we're currently loading planner data
      if (this.isLoadingPlannerData) {
        return;
      }

      const hotInstance = this.$refs.hotTable.hotInstance;
      const currentData = hotInstance.getData();

      // Find rows that have at least one non-empty cell
      const filledRows = [];
      currentData.forEach((row, index) => {
        const hasContent =
          row &&
          row.some((cell) => {
            return (
              cell !== null &&
              cell !== "" &&
              cell !== undefined &&
              String(cell).trim() !== ""
            );
          });
        if (hasContent) {
          filledRows.push(row);
        }
      });

      // Add only 3 empty rows at the end for new entries
      filledRows.push(["", "", "", ""]);
      filledRows.push(["", "", "", ""]);
      filledRows.push(["", "", "", ""]);

      // Force reload the data to ensure proper cleanup
      hotInstance.loadData(filledRows);

      // Update the plannerTableData as well
      this.plannerTableData = filledRows
        .filter(
          (row) =>
            row &&
            row.some(
              (cell) =>
                cell !== null && cell !== "" && String(cell).trim() !== ""
            )
        )
        .map((row) => ({
          id: 0,
          plannerDayWiseID: this.currentEditingPlannerId || 0,
          lessonOrganisation: row[0] || "",
          activity: row[1] || "",
          resource: row[2] || "",
          assessmentStrategy: row[3] || "",
        }));

    },

    // Specialized method for cleaning up after paste operations
    cleanupEmptyRowsAfterPaste() {
      if (!this.$refs.hotTable || !this.$refs.hotTable.hotInstance) return;

      // Don't cleanup if we're currently loading planner data
      if (this.isLoadingPlannerData) {
        return;
      }

      const hotInstance = this.$refs.hotTable.hotInstance;
      const currentData = hotInstance.getData();

      // Find all rows with content, using a more thorough check
      const processedRows = [];
      currentData.forEach((row, index) => {
        if (row && Array.isArray(row)) {
          // Check if the row has any non-empty content
          const hasContent = row.some((cell) => {
            if (cell === null || cell === undefined) return false;
            const cellStr = String(cell).trim();
            return (
              cellStr !== "" && cellStr !== "null" && cellStr !== "undefined"
            );
          });

          if (hasContent) {
            // Clean up the row data and ensure all cells are strings
            const cleanRow = row.map((cell) => {
              if (cell === null || cell === undefined) return "";
              return String(cell).trim();
            });
            processedRows.push(cleanRow);
          } else {
          }
        }
      });

      // If we found content, preserve it and add one spare row
      if (processedRows.length > 0) {
        // Add one empty row at the end for new input
        processedRows.push(["", "", "", ""]);
      } else {
        // If no content was found, start with one empty row
        processedRows.push(["", "", "", ""]);
      }

      // Reload the cleaned data
      hotInstance.loadData(processedRows);

      // Update the plannerTableData for Vue component tracking
      this.plannerTableData = processedRows
        .filter((row) => row.some((cell) => cell && String(cell).trim() !== ""))
        .map((row) => ({
          id: 0,
          plannerDayWiseID: this.currentEditingPlannerId || 0,
          lessonOrganisation: row[0] || "",
          activity: row[1] || "",
          resource: row[2] || "",
          assessmentStrategy: row[3] || "",
        }));

      // Show success message about the organized data
      if (this.plannerTableData.length > 0) {
        // this.$bvToast.toast(`Data organized into ${this.plannerTableData.length} row${this.plannerTableData.length === 1 ? '' : 's'}`, {
        //   title: "Data Organized",
        //   variant: "success",
        //   solid: true,
        // });
      }
    },

    // Debug method to examine clipboard data
    debugClipboardData() {
      navigator.clipboard
        .read()
        .then((clipboardItems) => {
          for (const clipboardItem of clipboardItems) {
            for (const type of clipboardItem.types) {
              clipboardItem
                .getType(type)
                .then((blob) => {
                  return blob.text();
                })
                .then((text) => {

                  // Show analysis in a toast
                  const hasNewlines = text.includes("\n");
                  const hasTabs = text.includes("\t");
                  const lineCount = text.split("\n").length;

                  this.$bvToast.toast(
                    `Clipboard Analysis: ${lineCount} lines, Tabs: ${hasTabs}, Newlines: ${hasNewlines}`,
                    {
                      title: "Clipboard Debug",
                      variant: "info",
                      solid: true,
                    }
                  );
                });
            }
          }
        })
        .catch((err) => {
          console.error("Failed to read clipboard contents: ", err);
        });
    },

    goBackToSubtopics() {
      this.showDailyPlanTable = false;
      this.dailyPlannerViewMode = false;
      this.selectedSubtopicForPlan = "";
      this.dailyPlannerForms = [];
      this.filterObj.subtopicID = null;
      this.loadSavedPlanners();
    },
    // Helper method to initialize table with clean data
    initializeTableWithCleanData(data = null) {
      if (!this.$refs.hotTable || !this.$refs.hotTable.hotInstance) return;

      const hotInstance = this.$refs.hotTable.hotInstance;

      if (data && data.length > 0) {
        // Load provided data
        hotInstance.loadData(data);
      } else {
        // Load minimal empty data
        hotInstance.loadData([
          ["", "", "", ""],
          ["", "", "", ""],
          ["", "", "", ""],
        ]);
      }

      // Clean up after loading
      setTimeout(() => {
        this.cleanupEmptyRows();
      }, 100);
    },
    getDynamicHotSettingsForForm(formIndex) {
      const vueComponent = this; // Store reference to Vue component
      const columnLayout = this.globalColumnLayout;
      const isViewOnly = this.dailyPlannerViewMode;

      // Set column widths based on layout
      let colWidths;
      if (columnLayout === 3) {
        // 3 columns: wider columns for better Urdu display
        colWidths = [350, 300, 350]; // Lesson Org, Activity, Resources
      } else {
        // 4 columns: standard widths
        colWidths = [200, 200, 200, 200];
      }

      return {
        licenseKey: "non-commercial-and-evaluation",
        readOnly: isViewOnly,
        minSpareRows: isViewOnly ? 0 : 3,
        stretchH: "all",
        width: "100%",
        height: 400,
        rowHeights: 100,
        colWidths: colWidths,
        colHeaders: this.getColumnHeaders(columnLayout), // Explicitly set column headers
        columns: this.getColumnHeaders(columnLayout).map((header, index) => ({
          data: index,
          type: "text",
          width: colWidths[index] || 200,
          readOnly: isViewOnly,
        })),
        contextMenu: !isViewOnly,
        copyPaste: !isViewOnly,
        allowInsertRow: !isViewOnly,
        allowInsertColumn: false,
        copyPasteMode: "cells",
        outsideClickDeselects: false,
        persistentState: true,
        beforeCopy: (data, coords) => {
        },
        beforePaste: (data, coords) => {

          const processedData = data.map((row) => {
            if (
              Array.isArray(row) &&
              row.length === 1 &&
              typeof row[0] === "string"
            ) {
              let cellContent = row[0];
              cellContent = cellContent
                .replace(/\r\n/g, "\n")
                .replace(/\r/g, "\n");

              if (cellContent.includes("\t")) {
                const splitByTabs = cellContent
                  .split("\t")
                  .map((cell) => cell.trim());
                const result = splitByTabs.slice(0, columnLayout);
                while (result.length < columnLayout) {
                  result.push("");
                }
                return result;
              }

              if (cellContent.includes("|")) {
                const splitByPipe = cellContent
                  .split("|")
                  .map((cell) => cell.trim());
                if (splitByPipe.length >= 2) {
                  const result = splitByPipe.slice(0, columnLayout);
                  while (result.length < columnLayout) {
                    result.push("");
                  }
                  return result;
                }
              }

              return [cellContent.trim(), "", "", ""].slice(0, columnLayout);
            }

            if (Array.isArray(row)) {
              const result = row
                .slice(0, columnLayout)
                .map((cell) => (cell ? cell.toString().trim() : ""));
              while (result.length < columnLayout) {
                result.push("");
              }
              return result;
            }

            return Array(columnLayout).fill("");
          });

          data.splice(0, data.length, ...processedData);
          return true;
        },
        afterPaste: (data, coords) => {
          this.syncTableDataToForm(formIndex);
        },
        afterChange: (changes, source) => {
          if (source === "loadData") return;
          this.syncTableDataToForm(formIndex);
        },
        cells: (row, col) => {
          const cellProperties = {};
          cellProperties.renderer = function (
            instance,
            td,
            row,
            col,
            prop,
            value,
            cellProperties
          ) {
            td.innerHTML = value || "";
            td.style.whiteSpace = "pre-wrap";
            td.style.overflow = "hidden";
            td.style.wordWrap = "break-word";
            td.style.verticalAlign = "top";
            td.style.padding = "8px";
            return td;
          };
          return cellProperties;
        },
        enterBeginsEditing: true,
        enterMoves: { row: 1, col: 0 },
        tabMoves: { row: 0, col: 1 },
        wordWrap: true,
        autoWrapRow: true,
        autoWrapCol: true,
      };
    },

    syncTableDataToForm(formIndex) {
      const refName = `hotTable_${formIndex}`;
      let hotTableRef = this.$refs[refName];

      if (Array.isArray(hotTableRef)) {
        hotTableRef = hotTableRef[0];
      }

      if (
        hotTableRef &&
        hotTableRef.hotInstance &&
        this.dailyPlannerForms[formIndex]
      ) {
        try {
          const tableData = hotTableRef.hotInstance.getData();
          this.dailyPlannerForms[formIndex].tableData = tableData;
        } catch (error) {
          console.error(
            `Error syncing table data for form ${formIndex}:`,
            error
          );
        }
      }
    },

    onTableDataChange(formIndex, changes, source) {
      if (source === "loadData") return;

      // Sync the data back to the form
      this.syncTableDataToForm(formIndex);
    },

    async saveAllPlannerForms() {
      if (this.dailyPlannerForms.length === 0) {
        this.$bvToast.toast("No lesson plans to save!", {
          title: this.$t("Error!"),
          variant: "warning",
          solid: true,
        });
        return;
      }

      let savedCount = 0;
      let failedCount = 0;

      // Save each form one by one with suppressed individual toasts
      for (let i = 0; i < this.dailyPlannerForms.length; i++) {
        try {
          await this.savePlannerForm(i, true); // Pass true to suppress individual toasts
          savedCount++;
        } catch (error) {
          console.error(`Failed to save lesson plan ${i + 1}:`, error);
          failedCount++;
        }
      }

      if (savedCount > 0) {
        await this.loadSavedPlanners();
      }

      // Show summary toast
      if (savedCount > 0 && failedCount === 0) {
        this.$bvToast.toast(
          `All ${savedCount} lesson plans saved successfully!`,
          {
            title: "Success!",
            variant: "success",
            solid: true,
          }
        );
      } else if (savedCount > 0 && failedCount > 0) {
        this.$bvToast.toast(
          `${savedCount} lesson plans saved, ${failedCount} failed`,
          {
            title: "Partial Success",
            variant: "warning",
            solid: true,
          }
        );
      } else {
        this.$bvToast.toast("Failed to save any lesson plans", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      }
    },
    exportPlannerRow(item) {
      // Get class, section, subject names
      const className = this.getClassName(this.myObj.clsID) || "Class";
      const sectionName =
        this.getSecName(this.myObj.clsID, this.secIDs) || "Sec";
      const subjectName = this.getSubName(this.myObj.subjectID) || "Subject";
      // Compose filename
      const filename = `${className}-${sectionName}-${subjectName}.xlsx`;
      // Prepare data with camelCase column headers
      const row = {
        plannerId: item.id || item.plannerDayWiseID || "",
        mainTopic: item.mainTopicName || "",
        subTopic: item.name || item.subTopicName || "",
        dayFrom: item.dayFromName || item.dtFrom || "",
        dayTo: item.dayToName || item.dtTo || "",
      };
      // Create worksheet and workbook
      const ws = XLSX.utils.json_to_sheet([row]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Planner");
      // Export
      XLSX.writeFile(wb, filename);
    },
    async openExportModal() {
      this.showExportModal = true;

      try {
        // Load saved planners data first to get actual planner records
        await this.loadSavedPlanners();

        // Log the loaded saved planner data with planner IDs and date ranges

        if (this.savedPlanners && this.savedPlanners.length > 0) {

          this.savedPlanners.forEach((planner, index) => {
            const plannerData = planner.plannerDayWise || planner;

            if (plannerData) {
              // Get topic and subtopic names
              const topic = this.topics.find(
                (t) => t.id === plannerData.mainTopicID
              );
              const subtopic = this.allSubtopics.find(
                (s) => s.id === plannerData.subtopicID
              );

              const topicName = topic
                ? topic.name
                : `Topic ID: ${plannerData.mainTopicID}`;
              const subtopicName = subtopic
                ? subtopic.name
                : `Subtopic ID: ${plannerData.subtopicID}`;

            }
          });

          // Create a summary table for easy viewing
          const summaryData = this.savedPlanners.map((planner, index) => {
            const plannerData = planner.plannerDayWise || planner;
            const topic = this.topics.find(
              (t) => t.id === plannerData.mainTopicID
            );
            const subtopic = this.allSubtopics.find(
              (s) => s.id === plannerData.subtopicID
            );

            return {
              Index: index + 1,
              "Planner ID": plannerData.id,
              Topic: topic ? topic.name : `ID: ${plannerData.mainTopicID}`,
              Subtopic: subtopic
                ? subtopic.name
                : `ID: ${plannerData.subtopicID}`,
              "From Date": plannerData.dtFrom
                ? plannerData.dtFrom.split("T")[0]
                : "N/A",
              "To Date": plannerData.dtTo
                ? plannerData.dtTo.split("T")[0]
                : "N/A",
              Duration: plannerData.duration || "N/A",
            };
          });

          console.table(summaryData);

          // Additional statistics
          const uniqueTopics = new Set(
            this.savedPlanners.map((p) => (p.plannerDayWise || p).mainTopicID)
          );
          const uniqueSubtopics = new Set(
            this.savedPlanners.map((p) => (p.plannerDayWise || p).subtopicID)
          );

        } else {
        }

        // Now load topics and subtopics from the planner data
        await this.loadExportTopicsFromPlannerData();
        await this.loadExportSubtopicsFromPlannerData();

        // Set default selections to "All" after a short delay to ensure options are loaded
        this.$nextTick(() => {
          setTimeout(() => {
            this.exportForm.mainTopicIDs = [0]; // Select "All Topics"
            this.exportForm.subTopicIDs = [0]; // Select "All Subtopics"
          }, 500);
        });
      } catch (error) {
        console.error("Error loading export data:", error);
        this.$bvToast.toast("Failed to load export data. Please try again.", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      }
    },

    resetExportForm() {
      this.exportForm = {
        fromDate: "",
        toDate: "",
        mainTopicIDs: [0], // Default to "All"
        subTopicIDs: [0], // Default to "All"
      };
    },

    async loadExportTopicsFromPlannerData() {
      try {

        // Extract unique main topics from the loaded planner data
        const uniqueTopicIds = new Set();
        const topicsMap = new Map();

        // Get topic IDs from savedPlanners (actual planner data)
        if (this.savedPlanners && Array.isArray(this.savedPlanners)) {
          this.savedPlanners.forEach((planner) => {
            if (planner.plannerDayWise && planner.plannerDayWise.mainTopicID) {
              uniqueTopicIds.add(planner.plannerDayWise.mainTopicID);
            } else if (planner.mainTopicID) {
              uniqueTopicIds.add(planner.mainTopicID);
            }
          });
        }

        // If we have current filter topic selected, add it as well
        if (this.filterObj.topicID) {
          uniqueTopicIds.add(this.filterObj.topicID);
        }

        // Get topic names from the topics array (loaded from main topic API)
        Array.from(uniqueTopicIds).forEach((topicId) => {
          const topic = this.topics.find((t) => t.id === topicId);
          if (topic) {
            topicsMap.set(topicId, {
              id: topic.id,
              topic: topic.name,
              mainTopic: topic.name,
            });
          }
        });

        this.exportTopics = Array.from(topicsMap.values());
      } catch (error) {
        console.error("Error loading export topics from planner data:", error);
        this.exportTopics = [];
      }
    },

    async loadExportSubtopicsFromPlannerData() {
      try {

        // Extract unique subtopics from the loaded planner data
        const uniqueSubtopicIds = new Set();
        const subtopicsMap = new Map();

        // Get subtopic IDs from savedPlanners (actual planner data)
        if (this.savedPlanners && Array.isArray(this.savedPlanners)) {
          this.savedPlanners.forEach((planner) => {
            if (planner.plannerDayWise && planner.plannerDayWise.subtopicID) {
              uniqueSubtopicIds.add(planner.plannerDayWise.subtopicID);
            } else if (planner.subtopicID) {
              uniqueSubtopicIds.add(planner.subtopicID);
            }
          });
        }

        // If we have current filter subtopic selected, add it as well
        if (this.filterObj.subtopicID) {
          uniqueSubtopicIds.add(this.filterObj.subtopicID);
        }

        // Get subtopic names from the subtopics array (loaded from subtopic API)
        Array.from(uniqueSubtopicIds).forEach((subtopicId) => {
          const subtopic = this.allSubtopics.find((s) => s.id === subtopicId);
          if (subtopic) {
            subtopicsMap.set(subtopicId, {
              id: subtopic.id,
              subTopic: subtopic.name,
              mainTopicID: subtopic.mainTopicID,
            });
          }
        });

        this.exportSubtopics = Array.from(subtopicsMap.values());
      } catch (error) {
        console.error(
          "Error loading export subtopics from planner data:",
          error
        );
        this.exportSubtopics = [];
      }
    },

    async exportPlannerData() {
      // Validate required fields
      if (!this.exportForm.fromDate || !this.exportForm.toDate) {
        this.$bvToast.toast("Please select both From Date and To Date!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      // Validate date range
      if (
        new Date(this.exportForm.fromDate) >= new Date(this.exportForm.toDate)
      ) {
        this.$bvToast.toast("To Date must be after From Date!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      this.exportLoading = true;

      try {
        // Get filtered topics and subtopics based on selections with proper relationship handling
        let filteredTopics = [];
        let filteredSubtopics = [];

        // Filter topics first
        if (
          this.exportForm.mainTopicIDs &&
          this.exportForm.mainTopicIDs.length > 0
        ) {
          if (this.exportForm.mainTopicIDs.includes(0)) {
            // "All Topics" selected
            filteredTopics = [...this.exportTopics];
          } else {
            // Specific topics selected
            filteredTopics = this.exportTopics.filter((topic) =>
              this.exportForm.mainTopicIDs.includes(topic.id)
            );
          }
        } else {
          filteredTopics = [...this.exportTopics];
        }

        // Filter subtopics based on the selected topics AND subtopic selection
        if (
          this.exportForm.subTopicIDs &&
          this.exportForm.subTopicIDs.length > 0
        ) {
          if (this.exportForm.subTopicIDs.includes(0)) {
            // "All Subtopics" selected - but only for the selected topics
            if (
              this.exportForm.mainTopicIDs &&
              this.exportForm.mainTopicIDs.length > 0 &&
              !this.exportForm.mainTopicIDs.includes(0)
            ) {
              // If specific topics are selected, only include subtopics belonging to those topics
              filteredSubtopics = this.exportSubtopics.filter((subtopic) =>
                this.exportForm.mainTopicIDs.includes(subtopic.mainTopicID)
              );
            } else {
              // If "All Topics" is selected, include all subtopics
              filteredSubtopics = [...this.exportSubtopics];
            }
          } else {
            // Specific subtopics selected
            filteredSubtopics = this.exportSubtopics.filter((subtopic) => {
              // Include subtopic if it's selected AND belongs to selected topics (if any specific topics are selected)
              const subtopicSelected = this.exportForm.subTopicIDs.includes(
                subtopic.id
              );

              if (
                this.exportForm.mainTopicIDs &&
                this.exportForm.mainTopicIDs.length > 0 &&
                !this.exportForm.mainTopicIDs.includes(0)
              ) {
                // If specific topics are selected, also check if subtopic belongs to those topics
                const belongsToSelectedTopic =
                  this.exportForm.mainTopicIDs.includes(subtopic.mainTopicID);
                return subtopicSelected && belongsToSelectedTopic;
              } else {
                // If "All Topics" is selected, just check if subtopic is selected
                return subtopicSelected;
              }
            });
          }
        } else {
          // No subtopics selected, but filter by selected topics if any
          if (
            this.exportForm.mainTopicIDs &&
            this.exportForm.mainTopicIDs.length > 0 &&
            !this.exportForm.mainTopicIDs.includes(0)
          ) {
            filteredSubtopics = this.exportSubtopics.filter((subtopic) =>
              this.exportForm.mainTopicIDs.includes(subtopic.mainTopicID)
            );
          } else {
            filteredSubtopics = [...this.exportSubtopics];
          }
        }

        // Generate Excel data from filtered results
        this.generateExcelFromFilters(filteredTopics, filteredSubtopics);
      } catch (error) {
        console.error("Error generating export data:", error);
        this.$bvToast.toast(
          "Failed to generate export data. Please try again.",
          {
            title: this.$t("Error!"),
            variant: "danger",
            solid: true,
          }
        );
      } finally {
        this.exportLoading = false;
      }
    },

    generateExcelFromFilters(topics, subtopics) {
      // Generate Excel data from actual planner data (dailyPlannerForms and savedPlanners)
      const excelData = [];

      // Collect all planner data that matches the topic/subtopic filters (NO DATE FILTERING)
      const allPlannerData = [];

      // Add data from dailyPlannerForms (currently active planning forms)
      this.dailyPlannerForms.forEach((form, index) => {

        if (form.id && form.dtFrom && form.dtTo) {
          // Get topic and subtopic IDs from current filter context
          const mainTopicID = this.filterObj.topicID || 0;
          const subtopicID = this.filterObj.subtopicID || 0;

          allPlannerData.push({
            id: form.id,
            dtFrom: form.dtFrom,
            dtTo: form.dtTo,
            dayFromName: form.dayFromName,
            dayToName: form.dayToName,
            duration: form.duration,
            mainTopicID: mainTopicID,
            subtopicID: subtopicID,
            source: "dailyPlannerForms",
          });
        } else {
        }
      });

      // Add data from savedPlanners (previously saved planner records)
      if (this.savedPlanners && Array.isArray(this.savedPlanners)) {
        this.savedPlanners.forEach((planner, index) => {
          const plannerData = planner.plannerDayWise || planner;

          if (
            plannerData &&
            plannerData.id &&
            plannerData.dtFrom &&
            plannerData.dtTo
          ) {
            // Parse dates properly (handle both ISO string and date-only formats)
            let plannerFromDateStr = plannerData.dtFrom;
            let plannerToDateStr = plannerData.dtTo;

            // If dates include time, extract just the date part
            if (plannerFromDateStr.includes("T")) {
              plannerFromDateStr = plannerFromDateStr.split("T")[0];
            }
            if (plannerToDateStr.includes("T")) {
              plannerToDateStr = plannerToDateStr.split("T")[0];
            }

            // NO DATE FILTERING - Include ALL planners regardless of their dates
            allPlannerData.push({
              id: plannerData.id,
              dtFrom: plannerFromDateStr,
              dtTo: plannerToDateStr,
              dayFromName: plannerData.dayFromName,
              dayToName: plannerData.dayToName,
              duration: plannerData.duration,
              mainTopicID: plannerData.mainTopicID,
              subtopicID: plannerData.subtopicID,
              source: "savedPlanners",
            });
          } else {
          }
        });
      } else {
      }

      allPlannerData.forEach((item, index) => {
      });

      // Filter by selected topics and subtopics ONLY (no date filtering)
      const filteredPlannerData = allPlannerData.filter((plannerItem) => {
        let topicMatch = true;
        let subtopicMatch = true;

        // Check topic filter - if "All Topics" (0) is selected, include all topics
        if (
          this.exportForm.mainTopicIDs &&
          this.exportForm.mainTopicIDs.length > 0
        ) {
          if (!this.exportForm.mainTopicIDs.includes(0)) {
            // Only filter if "All Topics" is NOT selected
            topicMatch = this.exportForm.mainTopicIDs.includes(
              plannerItem.mainTopicID
            );
          }
          // If "All Topics" (0) is selected, topicMatch remains true
        }

        // Check subtopic filter - if "All Subtopics" (0) is selected, include all subtopics
        if (
          this.exportForm.subTopicIDs &&
          this.exportForm.subTopicIDs.length > 0
        ) {
          if (!this.exportForm.subTopicIDs.includes(0)) {
            // Only filter if "All Subtopics" is NOT selected
            subtopicMatch = this.exportForm.subTopicIDs.includes(
              plannerItem.subtopicID
            );
          }
          // If "All Subtopics" (0) is selected, subtopicMatch remains true
        }

        const finalMatch = topicMatch && subtopicMatch;

        return finalMatch;
      });

      // Generate Excel rows from filtered planner data
      filteredPlannerData.forEach((plannerItem, index) => {
        // Get topic and subtopic names
        const topic = this.topics.find((t) => t.id === plannerItem.mainTopicID);
        const subtopic = this.allSubtopics.find(
          (s) => s.id === plannerItem.subtopicID
        );

        const topicName = topic
          ? topic.name
          : `Topic ID: ${plannerItem.mainTopicID}`;
        const subtopicName = subtopic
          ? subtopic.name
          : `Subtopic ID: ${plannerItem.subtopicID}`;

        // Format date range from the actual planner data
        const plannerDateRange = `${plannerItem.dtFrom} to ${plannerItem.dtTo}`;

        const excelRow = {
          "Planner ID": plannerItem.id,
          Date: `${this.exportForm.fromDate} to ${this.exportForm.toDate}`,
          "Planner Date Range": plannerDateRange,
          "Main Topic": topicName,
          "Sub Topic": subtopicName,
          Duration: plannerItem.duration || "",
        };

        excelData.push(excelRow);
      });

      if (excelData.length === 0) {
        this.$bvToast.toast(
          "No planner data found for the selected topic/subtopic filters!",
          {
            title: "No Data",
            variant: "warning",
            solid: true,
          }
        );
        return;
      }

      // Create filename with date range (for organization, not filtering)
      const fromDateFormatted = this.exportForm.fromDate.replace(/-/g, "");
      const toDateFormatted = this.exportForm.toDate.replace(/-/g, "");
      const filename = `PlannerData-${fromDateFormatted}-${toDateFormatted}.xlsx`;

      // Create workbook and export
      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Planner Data");

      // Auto-size columns for better readability
      const colWidths = [
        { wch: 12 }, // Planner ID
        { wch: 25 }, // Date
        { wch: 25 }, // Planner Date Range
        { wch: 20 }, // Main Topic
        { wch: 25 }, // Sub Topic
        { wch: 12 }, // Duration
      ];
      ws["!cols"] = colWidths;

      // Export
      XLSX.writeFile(wb, filename);

      // Close modal and show success
      this.showExportModal = false;
      this.$bvToast.toast(
        `Exported ${excelData.length} planner records for period ${this.exportForm.fromDate} to ${this.exportForm.toDate}!`,
        {
          title: "Export Successful",
          variant: "success",
          solid: true,
        }
      );
    },
    async openImportModal() {
      this.showImportModal = true;
    },
    resetImportForm() {
      this.importFile = null;
      this.importPreviewData = [];
    },
    async processImportFile() {
      if (!this.importFile) {
        this.$bvToast.toast("Please select a file first!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      try {
        const fileReader = new FileReader();

        fileReader.onload = (e) => {
          try {
            const data = e.target.result;
            const workbook = XLSX.read(data, { type: "binary" });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);

            // Process and validate the imported data
            this.importPreviewData = jsonData.map((row, index) => {
              const plannerId =
                row["Planner ID"] || row["plannerId"] || row["id"];

              // Handle the new "Date" column format which contains a date range
              let fromDate = "";
              let toDate = "";

              // Check for the new "Date" column first (contains "YYYY-MM-DD to YYYY-MM-DD")
              if (
                row["Date"] &&
                typeof row["Date"] === "string" &&
                row["Date"].includes(" to ")
              ) {
                const dateParts = row["Date"].split(" to ");
                if (dateParts.length === 2) {
                  fromDate = dateParts[0].trim();
                  toDate = dateParts[1].trim();
                }
              }

              // Fallback to individual columns if Date column not found or invalid
              if (!fromDate || !toDate) {
                fromDate =
                  row["From Date"] ||
                  row["fromDate"] ||
                  row["dtFrom"] ||
                  row["Planner From Date"] ||
                  "";
                toDate =
                  row["To Date"] ||
                  row["toDate"] ||
                  row["dtTo"] ||
                  row["Planner To Date"] ||
                  "";
              }

              let valid = true;
              let error = "";

              // Validate required fields
              if (!plannerId || plannerId === "" || isNaN(plannerId)) {
                valid = false;
                error = "Invalid Planner ID";
              } else if (!fromDate || fromDate === "") {
                valid = false;
                error = "Missing From Date";
              } else if (!toDate || toDate === "") {
                valid = false;
                error = "Missing To Date";
              } else {
                // Validate date format (should be YYYY-MM-DD or convertible to it)
                const fromDateObj = new Date(fromDate);
                const toDateObj = new Date(toDate);

                if (isNaN(fromDateObj.getTime())) {
                  valid = false;
                  error = "Invalid From Date format";
                } else if (isNaN(toDateObj.getTime())) {
                  valid = false;
                  error = "Invalid To Date format";
                } else if (fromDateObj >= toDateObj) {
                  valid = false;
                  error = "To Date must be after From Date";
                }
              }

              return {
                plannerId: parseInt(plannerId),
                fromDate: fromDate,
                toDate: toDate,
                valid: valid,
                error: error,
                rowIndex: index + 1,
                originalDateColumn: row["Date"] || "N/A",
              };
            });

            if (this.importPreviewData.length === 0) {
              this.$bvToast.toast("No valid data found in the file!", {
                title: "Import Error",
                variant: "warning",
                solid: true,
              });
            }
          } catch (parseError) {
            console.error("Error parsing Excel file:", parseError);
            this.$bvToast.toast(
              "Error reading the Excel file. Please check the file format.",
              {
                title: "Parse Error",
                variant: "danger",
                solid: true,
              }
            );
          }
        };

        fileReader.readAsBinaryString(this.importFile);
      } catch (error) {
        console.error("Error processing import file:", error);
        this.$bvToast.toast("Error processing the file. Please try again.", {
          title: "File Error",
          variant: "danger",
          solid: true,
        });
      }
    },

    async importPlannerData() {
      const validRecords = this.importPreviewData.filter(
        (record) => record.valid
      );

      if (validRecords.length === 0) {
        this.$bvToast.toast("No valid records to import!", {
          title: "Import Error",
          variant: "warning",
          solid: true,
        });
        return;
      }

      this.importLoading = true;

      try {
        // Prepare the data for the API call
        const updateData = validRecords.map((record) => ({
          id: record.plannerId,
          dtFrom: new Date(record.fromDate).toISOString(),
          dtTo: new Date(record.toDate).toISOString(),
        }));

        // Make the API call
        const apiUrl =
          this.$store.state.domain +
          `PlannerDayWise/UpdateDates?db=${this.$store.state.userData.db}&campusID=${this.$store.state.userData.cId}&userID=${this.$store.state.userData.userID}`;

        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.$store.state.userData.token}`,
          },
          body: JSON.stringify(updateData),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        // Close modal and show success
        this.showImportModal = false;
        this.$bvToast.toast(
          `Successfully updated ${validRecords.length} planner records!`,
          {
            title: "Import Successful",
            variant: "success",
            solid: true,
          }
        );

        // Refresh the planner data
        await this.loadSavedPlanners();
      } catch (error) {
        console.error("Error importing planner data:", error);
        this.$bvToast.toast("Failed to import data. Please try again.", {
          title: "Import Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.importLoading = false;
      }
    },
    // URL processing methods
    convertUrlsToAnchorTags(text) {
      if (!text) return "";

      // Enhanced regex to match various URL patterns
      const urlRegex =
        /(@?https?:\/\/[^\s]+|@?www\.[^\s]+|@?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}[^\s]*)/gi;

      return text.replace(urlRegex, (match) => {
        // Remove @ if present and create the actual URL
        const actualUrl = match.startsWith("@") ? match.substring(1) : match;
        const displayText = match;

        // Ensure URL has protocol
        const finalUrl = actualUrl.startsWith("http")
          ? actualUrl
          : `https://${actualUrl}`;

        return `<a href="${finalUrl}" target="_blank" rel="noopener noreferrer">${displayText}</a>`;
      });
    },

    stripHtmlTags(text) {
      if (!text) return "";

      // Create a temporary div to parse HTML and extract text content
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = text;
      return tempDiv.textContent || tempDiv.innerText || "";
    },

    processContentForSaving(content) {
      if (!content) return "";

      // Check if content already contains HTML anchor tags
      const hasExistingAnchors = /<a\s+href=/i.test(content);

      if (hasExistingAnchors) {
        // If content already has anchor tags, return as is to prevent double wrapping
        return content;
      }

      // Only process URLs if no existing anchor tags are found
      return this.convertUrlsToAnchorTags(content);
    },

    processContentForEditing(content) {
      if (!content) return "";
      return this.stripHtmlTags(content);
    },

    // Column layout toggle methods
    toggleGlobalColumnLayout() {
      // Toggle between 4 and 3 columns for all forms
      this.globalColumnLayout = this.globalColumnLayout === 4 ? 3 : 4;

      // Update all forms to use the new global layout
      this.dailyPlannerForms.forEach((form, index) => {
        this.updateTableDataForGlobalLayout(index);
      });

      // Force Vue to update the component
      this.$forceUpdate();

    },

    updateTableDataForGlobalLayout(formIndex) {
      const form = this.dailyPlannerForms[formIndex];
      if (!form) return;

      const currentData = form.tableData;
      const newData = [];

      currentData.forEach((row) => {
        if (this.globalColumnLayout === 3) {
          // 3 columns: Lesson Organisation, Activity, Resources (hide Assessment)
          const lessonOrg = row[0] || "";
          const activity = row[1] || "";
          const resource = row[2] || "";
          // Assessment (row[3]) is hidden in 3-column mode

          newData.push([lessonOrg, activity, resource]);
        } else {
          // Keep original 4 columns
          newData.push([
            row[0] || "",
            row[1] || "",
            row[2] || "",
            row[3] || "",
          ]);
        }
      });

      form.tableData = newData;

      // Force reinitialize the table with new column layout
      this.$nextTick(() => {
        this.initializeNewTable(formIndex);
      });
    },

    getColumnHeaders(columnLayout) {
      if (columnLayout === 3) {
        // 3 columns for better Urdu display - hide assessment
        return ["Lesson Organisation", "Activity", "Resources"];
      } else {
        // Default 4 columns
        return [
          "Lesson Organisation",
          "Activity",
          "Resource",
          "Assessment Strategy",
        ];
      }
    },

    updateTableDataForColumnLayout(formIndex) {
      const form = this.dailyPlannerForms[formIndex];
      if (!form) return;

      const currentData = form.tableData;
      const newData = [];

      currentData.forEach((row) => {
        if (form.columnLayout === 3) {
          // 3 columns: Lesson Organisation, Activity, Resources (hide Assessment)
          const lessonOrg = row[0] || "";
          const activity = row[1] || "";
          const resource = row[2] || "";
          // Assessment (row[3]) is hidden in 3-column mode

          newData.push([lessonOrg, activity, resource]);
        } else {
          // Keep original 4 columns
          newData.push([
            row[0] || "",
            row[1] || "",
            row[2] || "",
            row[3] || "",
          ]);
        }
      });

      form.tableData = newData;
    },
  },
  watch: {
    // Watch for changes in each form's dates and automatically set the day names
    dailyPlannerForms: {
      handler(newForms) {
        if (newForms && newForms.length > 0) {
          newForms.forEach((form, index) => {
            if (form.dtFrom) {
              const fromDate = new Date(form.dtFrom);
              const dayNames = [
                "Sunday",
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
              ];
              form.dayFromName = dayNames[fromDate.getDay()];
            }

            if (form.dtTo) {
              const toDate = new Date(form.dtTo);
              const dayNames = [
                "Sunday",
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
              ];
              form.dayToName = dayNames[toDate.getDay()];
            }
          });
        }
      },
      deep: true,
      immediate: true,
    },

    // Watch for changes in secIDs to debug selection issues
    secIDs: {
      handler(newVal, oldVal) {
      },
      immediate: true,
    },

    // Watch for changes in currentSections to ensure proper selection
    currentSections: {
      handler(newSections, oldSections) {

        // If we have sections and secIDs is null/undefined, auto-select "All"
        if (
          newSections &&
          newSections.length > 0 &&
          (this.secIDs === null || this.secIDs === undefined)
        ) {
          this.$nextTick(() => {
            this.secIDs = 0;
          });
        }
      },
      immediate: true,
    },

    // Watch for changes in selected main topics to update subtopic options
    "exportForm.mainTopicIDs": {
      handler(newTopicIDs, oldTopicIDs) {

        // Reset subtopic selection when main topics change
        if (newTopicIDs && newTopicIDs.length > 0) {
          // Clear current subtopic selection and set to "All"
          this.exportForm.subTopicIDs = [0]; // Reset to "All Subtopics"
        }
      },
      deep: true,
    },

    // Watch for import file changes to auto-process
    importFile: {
      handler(newFile) {
        if (newFile) {
          // Auto-process the file when it's selected
          this.$nextTick(() => {
            this.processImportFile();
          });
        } else {
          // Clear preview data when file is removed
          this.importPreviewData = [];
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../planner/plannerListTable.scss";

.per-page-selector {
  width: 90px;
}

.quill-editor ::v-deep {
  > .ql-container {
    height: 200px;
  }
}

// Card styling
.card {
  border: 1px solid rgba(224, 230, 237, 0.5);
  box-shadow: 0 2px 6px 0 rgba(67, 89, 113, 0.12);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 25px 0 rgba(67, 89, 113, 0.15);
  }
}

// Button styling
.btn {
  border-radius: 0.357rem;
  font-weight: 500;
  transition: all 0.3s ease;

  &.btn-primary {
    box-shadow: 0 3px 10px 0 rgba(115, 103, 240, 0.3);

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 5px 15px 0 rgba(115, 103, 240, 0.4);
    }
  }

  &.btn-success {
    box-shadow: 0 3px 10px 0 rgba(40, 199, 111, 0.3);

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 5px 15px 0 rgba(40, 199, 111, 0.4);
    }
  }

  &.btn-outline-primary {
    border-color: #7367f0;
    color: #7367f0;

    &:hover {
      background-color: #7367f0;
      border-color: #7367f0;
      transform: translateY(-1px);
    }
  }

  &.btn-outline-secondary {
    &:hover {
      transform: translateY(-1px);
    }
  }
}

// Badge styling
.badge {
  font-weight: 500;
  border-radius: 0.357rem;

  &.badge-light-primary {
    color: #7367f0;
    background-color: rgba(115, 103, 240, 0.12);
  }

  &.badge-light-success {
    color: #28c76f;
    background-color: rgba(40, 199, 111, 0.12);
  }

  &.badge-light-info {
    color: #00cfe8;
    background-color: rgba(0, 207, 232, 0.12);
  }
}

.planner-filter-wrap,
.subtopics-planner-wrap,
.daily-planner-nav-wrap,
.daily-overview-wrap,
.daily-lesson-wrap {
  position: relative;
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 20px;
  padding: 1.25rem 1.35rem 1.35rem;
  box-shadow:
    0 4px 6px rgba(115, 103, 240, 0.06),
    0 12px 28px rgba(115, 103, 240, 0.14),
    0 24px 48px rgba(45, 27, 105, 0.06);
  margin-bottom: 1rem;
}

.daily-planner-nav-wrap {
  padding: 0.9rem 1.15rem;
}

.daily-planner-nav-inner {
  display: flex;
  align-items: center;
}

.daily-back-btn {
  font-weight: 600;
  border-radius: 10px;
  border-color: #d9d0ff !important;
  color: #4a4568 !important;
  background: #fff !important;
  box-shadow: 0 2px 6px rgba(115, 103, 240, 0.08);

  &:hover {
    background: #f8f6ff !important;
    border-color: #7367f0 !important;
    color: #7367f0 !important;
  }
}

.daily-overview-card {
  background: transparent !important;
  box-shadow: none !important;
}

.daily-overview-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.15rem;
  flex-wrap: wrap;

  .resource-title {
    color: #2d1b69;
    font-weight: 800;
    letter-spacing: -0.3px;
  }
}

.daily-overview-header-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.overview-topic-badge,
.overview-count-badge {
  font-weight: 600;
  padding: 0.45rem 0.85rem;
  border-radius: 999px;
  box-shadow: 0 4px 12px rgba(115, 103, 240, 0.1);
}

.daily-overview-meta-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
  margin-bottom: 1.15rem;
}

.overview-meta-item {
  background: #fff;
  border: 1px solid #e4deff;
  border-radius: 14px;
  padding: 0.85rem 1rem;
  box-shadow: 0 2px 8px rgba(115, 103, 240, 0.06);
}

.overview-meta-item--wide {
  grid-column: 1 / -1;
}

.overview-meta-label {
  display: block;
  color: #6e6b7b;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.35px;
  margin-bottom: 0.35rem;
}

.overview-meta-value {
  display: block;
  color: #2d1b69;
  font-weight: 700;
  font-size: 0.95rem;
  line-height: 1.35;
}

.daily-overview-section + .daily-overview-section {
  margin-top: 1rem;
}

.overview-section-label {
  display: block;
  color: #4a4568;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.35px;
  margin-bottom: 0.65rem;
}

.overview-tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.overview-tag-badge {
  font-weight: 600;
  padding: 0.45rem 0.8rem;
  border-radius: 999px;
}

.overview-objectives-box {
  background: #fff;
  border: 1px solid #e4deff;
  border-radius: 14px;
  padding: 0.95rem 1rem;
  color: #4a4568;
  line-height: 1.55;
  box-shadow: 0 2px 8px rgba(115, 103, 240, 0.06);
}

@media (max-width: 768px) {
  .daily-overview-meta-grid {
    grid-template-columns: 1fr;
  }

  .overview-meta-item--wide {
    grid-column: auto;
  }

  .daily-overview-header-actions {
    width: 100%;
  }

  .daily-lesson-header {
    flex-direction: column;
    align-items: flex-start !important;
  }

  .daily-lesson-actions {
    flex-direction: column;
    align-items: stretch;
  }
}

.daily-lesson-card {
  background: transparent !important;
  box-shadow: none !important;
}

.daily-lesson-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.15rem;
  flex-wrap: wrap;

  .resource-title {
    color: #2d1b69;
    font-weight: 800;
    letter-spacing: -0.3px;
  }
}

.daily-lesson-state {
  text-align: center;
  padding: 2.5rem 1.25rem;
  border-radius: 16px;
  border: 1px dashed #d9d0ff;
  background: #fff;
}

.daily-lesson-state-icon {
  color: #b8b0d9;
}

.daily-lesson-forms {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.daily-lesson-plan-card {
  margin-bottom: 0 !important;
}

.daily-lesson-plan-inner {
  border-radius: 16px !important;
  border: 1px solid #e4deff !important;
  background: #fff !important;
  box-shadow: 0 2px 8px rgba(115, 103, 240, 0.06) !important;
  overflow: hidden;
}

.daily-lesson-plan-header h6 {
  color: #2d1b69;
  font-weight: 700;
}

.daily-lesson-plan-inner ::v-deep .card-header {
  background: linear-gradient(180deg, #f8f6ff 0%, #ffffff 100%) !important;
  border-bottom: 1px solid #ebe6ff !important;
  padding: 0.95rem 1.15rem !important;
}

.daily-lesson-field-label {
  color: #4a4568 !important;
  font-weight: 700 !important;
  font-size: 0.78rem !important;
  text-transform: uppercase;
  letter-spacing: 0.35px;
}

.daily-lesson-section-title {
  color: #2d1b69;
  font-weight: 700;
}

.daily-lesson-table-wrap {
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #e4deff;
  box-shadow: 0 2px 8px rgba(115, 103, 240, 0.06);
  background: #fff;
}

.daily-lesson-actions {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1.25rem;
  padding-top: 1.15rem;
  border-top: 1px solid #ebe6ff;
}

.daily-lesson-action-btn {
  min-width: 170px;
  font-weight: 600;
  border-radius: 10px;
}

.planner-filter-wrap {
  overflow: visible;
}

.planner-filter-card {
  background: transparent !important;
  box-shadow: none !important;
  overflow: visible !important;
}

.planner-filter-header {
  .resource-title {
    color: #2d1b69;
    font-weight: 800;
    letter-spacing: -0.3px;
  }
}

.planner-filter-label {
  color: #4a4568 !important;
  font-weight: 700 !important;
  font-size: 0.78rem !important;
  text-transform: uppercase;
  letter-spacing: 0.35px;
  margin-bottom: 0.35rem !important;
}

.planner-filter-row {
  overflow: visible;

  ::v-deep .v-select {
    .vs__dropdown-toggle {
      min-height: 42px;
      border-radius: 10px !important;
      border: 1.5px solid #d9d0ff !important;
      background: #fff !important;
      box-shadow: 0 2px 6px rgba(115, 103, 240, 0.06);
    }

    .vs__selected {
      color: #2d1b69 !important;
      font-weight: 600;
      font-size: 0.875rem;
    }

    .vs__search::placeholder {
      color: #9b94b8;
    }
  }
}

.subtopics-planner-wrap {
  margin-bottom: 1.25rem;
  overflow: hidden;
}

.subtopics-planner-card {
  background: transparent !important;
  box-shadow: none !important;
}

.subtopics-header {
  .resource-title {
    color: #2d1b69;
    font-weight: 800;
    letter-spacing: -0.3px;
  }
}

.subtopics-count-badge {
  font-weight: 600;
  padding: 0.45rem 0.85rem;
  border-radius: 999px;
  box-shadow: 0 4px 12px rgba(115, 103, 240, 0.12);
}

.subtopics-skeleton-wrap,
.subtopics-table-wrap {
  border-radius: 16px;
  overflow: hidden;
  box-shadow:
    0 2px 8px rgba(67, 89, 113, 0.06),
    0 8px 24px rgba(115, 103, 240, 0.1);
  border: 1px solid #e4deff;
  background: #fff;
}

.subtopics-skeleton-wrap {
  padding: 0.15rem;
}

.subtopics-skeleton-table {
  border-radius: 14px;
  overflow: hidden;

  th {
    background: linear-gradient(180deg, #f3f0ff 0%, #ebe6ff 100%);
    border-bottom: 2px solid #ddd4ff;
    padding: 0.95rem 1rem;
    color: #4a4568;
    font-weight: 700;
    text-transform: uppercase;
    font-size: 0.72rem;
    letter-spacing: 0.4px;
  }

  td {
    padding: 1rem;
    background: #fff;
  }

  tr:nth-child(even) td {
    background: #faf9ff;
  }
}

.subtopics-table {
  margin-bottom: 0 !important;

  ::v-deep thead th {
    background: linear-gradient(180deg, #f3f0ff 0%, #ebe6ff 100%);
    color: #3d3560;
    font-weight: 700;
    text-transform: uppercase;
    font-size: 0.72rem;
    letter-spacing: 0.45px;
    border-top: none;
    border-bottom: 2px solid #ddd4ff;
    white-space: nowrap;
    padding: 0.95rem 1rem;
  }

  ::v-deep thead th:first-child {
    border-top-left-radius: 14px;
  }

  ::v-deep thead th:last-child {
    border-top-right-radius: 14px;
  }

  ::v-deep tbody tr {
    cursor: default;
    transition: background 0.2s ease, box-shadow 0.2s ease;

    &:nth-child(even) {
      background: #faf9ff;
    }

    &:hover {
      background: #f0ecff !important;
      box-shadow: inset 4px 0 0 #7367f0;
    }

    td {
      vertical-align: middle;
      border-color: #ede9f7;
      padding: 0.9rem 1rem;
    }
  }
}

.topic-pill {
  display: inline-block;
  max-width: 100%;
  padding: 0.4rem 0.8rem;
  border-radius: 10px;
  background: #dbeafe;
  color: #1e40af;
  font-weight: 700;
  font-size: 0.84rem;
  line-height: 1.35;
  border: 1px solid #93c5fd;
  box-shadow: 0 1px 3px rgba(30, 64, 175, 0.08);
  word-break: break-word;
}

.subtopic-name-text {
  display: block;
  color: #1f1635;
  font-weight: 600;
  font-size: 0.92rem;
  line-height: 1.45;
  word-break: break-word;
}

.subtopic-sno {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 30px;
  border-radius: 9px;
  background: #7367f0;
  color: #fff;
  font-weight: 700;
  font-size: 0.8rem;
  box-shadow: 0 2px 6px rgba(115, 103, 240, 0.35);
}

.skeleton-loader--sm {
  height: 14px;
  width: 70%;
}

.status-pill {
  font-weight: 600;
  padding: 0.4rem 0.7rem;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  font-size: 0.8rem;

  &--planned {
    color: #166534 !important;
    background-color: #dcfce7 !important;
    border: 1px solid #86efac;
  }

  &--pending {
    color: #6b6578 !important;
    background-color: #eceaf1 !important;
    border: 1px solid #d5d0de;
  }
}

.subtopic-actions {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.action-btn-view,
.action-btn-add {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-weight: 600;
  border-radius: 8px;
  padding: 0.4rem 0.85rem;
  transition: all 0.2s ease;

  span {
    line-height: 1;
  }
}

.action-btn-view {
  border-width: 1.5px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 207, 232, 0.25);
  }
}

.action-btn-add {
  box-shadow: 0 3px 10px rgba(115, 103, 240, 0.3);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 5px 14px rgba(115, 103, 240, 0.4);
  }
}

.subtopics-header-actions {
  flex-wrap: wrap;
  gap: 0.5rem;
}

.replicate-action-wrap {
  display: inline-flex;
  align-items: center;
}

.selection-dot-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  z-index: 2;
  min-width: 22px;
  height: 22px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  padding: 0 0.35rem;
}

.replicate-btn {
  border-color: #7367f0 !important;
  color: #7367f0 !important;
  background: #fff !important;
  font-weight: 600;

  ::v-deep svg {
    stroke: currentColor;
  }

  &:hover:not(:disabled),
  &:focus:not(:disabled),
  &:active:not(:disabled),
  &.active:not(:disabled) {
    background: #7367f0 !important;
    border-color: #7367f0 !important;
    color: #fff !important;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.35);

    ::v-deep svg {
      stroke: #fff;
    }
  }
}

.replicate-stat-box {
  border: 1px solid #ebe9f1;
  border-radius: 10px;
  padding: 0.65rem 0.5rem;
  background: #fff;

  &--success {
    border-color: #bbf7d0;
    background: #f0fdf4;
  }

  &--warning {
    border-color: #fde68a;
    background: #fffbeb;
  }

  &--danger {
    border-color: #fecaca;
    background: #fef2f2;
  }
}

::v-deep .replicate-modal {
  .modal-dialog,
  .modal-content,
  .modal-body {
    overflow: visible;
  }

  .modal-footer {
    position: relative;
    z-index: 0;
  }
}

.replicate-form {
  position: relative;
  z-index: 1;
}

::v-deep .replicate-session-select {
  .vs__dropdown-toggle {
    min-height: 42px;
    border-color: #d8d6de;
    background: #fff;
  }

  .vs__selected,
  .vs__search {
    color: #2f2b3d;
    font-size: 0.95rem;
  }
}

.replicate-stat-value {
  font-size: 1.15rem;
  font-weight: 700;
  color: #2d1b69;
}

.replicate-stat-label {
  font-size: 0.72rem;
  color: #6e6b7b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.mode-badge {
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.65rem;
  border-radius: 999px;
}

.view-only-field {
  background-color: #f8f8f8 !important;
  cursor: default;
}

.handsontable-view-only ::v-deep .handsontable td {
  background-color: #fafafa !important;
}

.planner-form-card.view-mode ::v-deep .card-header {
  background: linear-gradient(
    135deg,
    rgba(255, 159, 67, 0.08) 0%,
    rgba(255, 255, 255, 1) 100%
  );
}

// Table styling
::v-deep .table {
  tbody {
    tr {
      transition: all 0.3s ease;
    }
  }

  th {
    border-top: none;
    font-weight: 600;
    color: #5e5873;
    font-size: 0.857rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

// Form styling
::v-deep .form-group {
  margin-bottom: 1.5rem;

  label {
    color: #5e5873;
    font-size: 0.857rem;
    margin-bottom: 0.5rem;
  }
}

// Empty state styling
.text-center {
  .b-icon {
    transition: all 0.3s ease;
  }
}

// Lesson planning section
.lesson-planning-section {
  .table-responsive {
    border-radius: 0.357rem;
    border: 1px solid rgba(224, 230, 237, 0.5);
    overflow: hidden;
  }
}

// Overview icons
.d-flex .b-icon {
  transition: all 0.3s ease;
}

// Loading state
.spinner-grow {
  animation-duration: 1.5s;
}

// Date inputs
::v-deep .flatpickr-input {
  border-radius: 0.357rem;
  border: 1px solid #d8d6de;
  transition: all 0.3s ease;

  &:focus {
    border-color: #7367f0;
    box-shadow: 0 3px 10px 0 rgba(115, 103, 240, 0.3);
  }
}

// HandsOnTable styling
::v-deep .handsontable {
  .ht_master {
    .wtHolder {
      border: 1px solid rgba(224, 230, 237, 0.5);
      border-radius: 0.357rem;
    }
  }

  .rowHeader {
    background-color: #f8f8f8;
    font-weight: 600;
  }

  td {
    border-color: rgba(224, 230, 237, 0.5);

    &.area {
      background-color: rgba(115, 103, 240, 0.1);
    }
  }
}

// Vue-select styling
::v-deep .vs__dropdown-toggle {
  border: 1px solid #d8d6de;
  border-radius: 0.357rem;

  &:hover {
    border-color: #7367f0;
  }
}

// Responsive improvements
@media (max-width: 768px) {
  .d-flex.justify-content-between {
    flex-direction: column;
    align-items: stretch !important;

    .btn {
      margin-top: 1rem;
      width: 100%;
    }
  }

  .lesson-planning-section {
    .d-flex.justify-content-between {
      flex-direction: column;

      h6 {
        margin-bottom: 0.5rem;
      }

      small {
        text-align: center;
        margin-bottom: 1rem;
      }
    }
  }
}

// Button group styling
.d-flex.gap-2 {
  gap: 0.5rem;

  .btn {
    white-space: nowrap;
  }
}

// Improved HandsOnTable styling
::v-deep .handsontable {
  border-radius: 0.357rem;
  overflow: hidden;

  .ht_master {
    .wtHolder {
      border: 1px solid rgba(224, 230, 237, 0.5);
      border-radius: 0.357rem;
    }
  }

  .rowHeader {
    background-color: #f8f8f8;
    font-weight: 600;
    border-right: 1px solid rgba(224, 230, 237, 0.5);
  }

  .colHeader {
    background-color: #f8f8f8;
    font-weight: 600;
    border-bottom: 1px solid rgba(224, 230, 237, 0.5);
    text-align: center;
  }

  td {
    border-color: rgba(224, 230, 237, 0.5);

    &.area {
      background-color: rgba(115, 103, 240, 0.1);
    }

    &.current {
      background-color: rgba(115, 103, 240, 0.2);
    }
  }

  // Paste selection styling
  .ht__selection--area {
    background-color: rgba(40, 199, 111, 0.1);
    border: 2px solid #28c76f;
  }

  // Loading skeleton animations
  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.3;
    }
  }

  .pulse-animation {
    animation: pulse 1.5s ease-in-out infinite;
  }
}

.subtopic-cell {
  white-space: normal !important;
  word-break: break-word !important;
  vertical-align: middle !important;
}

.actions-cell {
  min-width: 160px;
  max-width: 200px;
  vertical-align: middle !important;
}

@media (max-width: 900px) {
  .subtopic-cell {
    min-width: 180px !important;
    max-width: 300px !important;
  }
  .actions-cell {
    min-width: 120px !important;
    max-width: 150px !important;
  }
}

// Export loading overlay styles
.export-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
  border-radius: 0.375rem;

  .loading-content {
    text-align: center;
    padding: 2rem;

    .b-spinner {
      animation-duration: 1.2s;
    }

    h6 {
      font-weight: 600;
      margin-bottom: 0.5rem;
    }

    p {
      font-size: 0.875rem;
      opacity: 0.8;
    }
  }
}

.form-disabled {
  pointer-events: none;
  opacity: 0.6;
  transition: opacity 0.3s ease;
}

// Modal positioning for overlay
::v-deep .modal-body {
  position: relative;
}

// Import loading overlay styles
.import-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
  border-radius: 0.375rem;

  .loading-content {
    text-align: center;
    padding: 2rem;

    .b-spinner {
      animation-duration: 1.2s;
    }

    h6 {
      font-weight: 600;
      margin-bottom: 0.5rem;
    }

    p {
      font-size: 0.875rem;
      opacity: 0.8;
    }
  }
}

// Enhanced planner form card styling
.planner-form-card {
  .card {
    border-radius: 12px !important;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08) !important;
    border: 1px solid #e9ecef !important;
    transition: all 0.3s ease !important;

    &:hover {
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12) !important;
      transform: translateY(-2px) !important;
    }

    .card-header {
      background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%) !important;
      border-bottom: 2px solid #e9ecef !important;
      padding: 1rem 1.5rem !important;

      h6 {
        color: #2c3e50 !important;
        font-weight: 700 !important;
        margin: 0 !important;
      }

      // Ensure remove button is properly aligned to the right
      .d-flex.justify-content-between {
        width: 100% !important;

        .btn-outline-danger {
          margin-left: auto !important;
          flex-shrink: 0 !important;
          margin-right: 0.5rem !important;
        }
      }
    }
  }
}

// Remove button styling
.btn-outline-danger {
  border-radius: 6px !important;
  font-weight: 500 !important;
  padding: 0.375rem 0.75rem !important;
  font-size: 0.875rem !important;
  transition: all 0.3s ease !important;
  border: 1px solid #dc3545 !important;
  color: #dc3545 !important;
  background: transparent !important;

  &:hover {
    background: #dc3545 !important;
    color: #ffffff !important;
    transform: translateY(-1px) !important;
    box-shadow: 0 4px 12px rgba(220, 53, 69, 0.3) !important;
  }

  &:focus {
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
  }
}

/* Responsive adjustments for upload show */
@media (max-width: 768px) {
  .upload-show-content {
    max-height: 60vh;
  }

  .link-actions {
    flex-direction: column;
  }

  .link-actions .btn {
    width: 100%;
    justify-content: center;
  }
}

/* Custom Toggle Switch Styles */
.custom-toggle-switch {
  position: relative;
  display: inline-block;
}

.toggle-input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 8px 16px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 2px solid #dee2e6;
  border-radius: 25px;
  transition: all 0.3s ease;
  font-size: 14px;
  font-weight: 500;
  color: #6c757d;
  min-width: 140px;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.toggle-label::before {
  content: "";
  position: absolute;
  top: 2px;
  left: 2px;
  width: calc(50% - 4px);
  height: calc(100% - 4px);
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  border-radius: 20px;
  transition: all 0.3s ease;
  z-index: 1;
}

.toggle-input:checked + .toggle-label {
  background: linear-gradient(135deg, #f3f0ff 0%, #e6d7ff 100%);
  border-color: #8b5cf6;
  color: #6d28d9;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);
}

.toggle-input:checked + .toggle-label::before {
  transform: translateX(100%);
}

.toggle-text {
  position: relative;
  z-index: 2;
  transition: all 0.3s ease;
  font-weight: 600;
}

.toggle-input:checked + .toggle-label .toggle-text {
  color: #6d28d9;
}

.toggle-label:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.toggle-input:checked + .toggle-label:hover {
  box-shadow: 0 6px 16px rgba(139, 92, 246, 0.4);
}

/* Focus styles for accessibility */
.toggle-input:focus + .toggle-label {
  outline: 2px solid #8b5cf6;
  outline-offset: 2px;
}

/* Responsive adjustments for toggle */
@media (max-width: 768px) {
  .toggle-label {
    min-width: 120px;
    font-size: 13px;
    padding: 6px 12px;
  }

  .toggle-text {
    font-size: 12px;
  }
}

.enhanced-toggle-switch {
  position: relative;
  display: inline-block;
}

.enhanced-toggle-input {
  opacity: 0;
  width: 0;
  height: 0;
}

.enhanced-toggle-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  width: 160px;
  height: 40px;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 25px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.toggle-slider {
  position: absolute;
  top: 2px;
  left: 2px;
  width: calc(50% - 2px);
  height: calc(100% - 4px);
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border-radius: 20px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1;
  box-shadow: 0 2px 6px rgba(59, 130, 246, 0.3);
}

.enhanced-toggle-input:checked + .enhanced-toggle-label .toggle-slider {
  transform: translateX(100%);
}

.toggle-text-en,
.toggle-text-ur {
  position: relative;
  z-index: 2;
  font-weight: 600;
  font-size: 13px;
  transition: all 0.3s ease;
  padding: 0 12px;
  text-align: center;
  flex: 1;
}

.toggle-text-en {
  color: #1f2937;
}

.toggle-text-ur {
  color: #1f2937;
  font-family: "Noto Nastaliq Urdu", serif;
}

/* When toggle is checked (3-column mode - Urdu active) */
.enhanced-toggle-input:checked + .enhanced-toggle-label .toggle-text-en {
  color: #1f2937;
}

.enhanced-toggle-input:checked + .enhanced-toggle-label .toggle-text-ur {
  color: #ffffff;
}

/* When toggle is unchecked (4-column mode - English active) */
.enhanced-toggle-input:not(:checked) + .enhanced-toggle-label .toggle-text-en {
  color: #ffffff;
}

.enhanced-toggle-input:not(:checked) + .enhanced-toggle-label .toggle-text-ur {
  color: #1f2937;
}

.enhanced-toggle-label:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  border-color: #cbd5e0;
  background: #ffffff;
}

.enhanced-toggle-input:checked + .enhanced-toggle-label {
  border-color: #3b82f6;
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.25);
  background: #f8fafc;
}

.enhanced-toggle-input:checked + .enhanced-toggle-label:hover {
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.35);
}

/* Focus styles for accessibility */
.enhanced-toggle-input:focus + .enhanced-toggle-label {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .enhanced-toggle-label {
    width: 140px;
    height: 36px;
  }

  .toggle-text-en,
  .toggle-text-ur {
    font-size: 12px;
    padding: 0 8px;
  }
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
@import "@core/scss/vue/libs/quill.scss";

.handsontable {
  font-size: 14px;

  .htCore {
    td {
      padding: 8px;
      vertical-align: top;
    }
  }
}
</style>
